import { useTranslation } from "@client-app/i18n";
import { getIntegrationBrandIcon } from "@client-app/lib/integration-brand-icons";
import { getMemberAvatarUrl, useAuth } from "@/contexts/AuthContext";
import { Permission } from "@/contexts/AuthContext/permissions";
import { useAdminContext } from "@/contexts/AdminContext";
import { useToast } from "@/contexts/ToastContext";
import { getAdminProductFeatures } from "@/utils/adminProductFeatures";
import { Tooltip } from "@common/index";
import {
  featureService,
  type WorkspaceDataRole,
} from "@common/api/featureService";
import {
  projectService,
  type ProjectUserResourceGrant,
  type ProjectIntegrationsBootstrapResponse,
} from "@common/api/brandGroupService";
import DetailDrawer from "@/components/DetailDrawer/DetailDrawer";
import ContactSalesModal from "../../Pricing/ContactSalesModal";
import {
  ArrowLeft,
  Briefcase,
  CaretDown,
  Check,
  CheckCircle,
  CircleNotch,
  DotsThree,
  LockKey,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Question,
  SquaresFour,
  Storefront,
  Trash,
  Upload,
  User,
  UserCirclePlus,
  Users,
  WarningCircle,
} from "@phosphor-icons/react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { buildAdminSettingsPath } from "../../../settingsRoute";
import { useBrandGroup } from "../../../contexts/BrandGroupContext";
import {
  getProjectIntegrationsBootstrapCached,
  invalidateProjectIntegrationsBootstrapCache,
} from "../../../contexts/projectIntegrationsBootstrapCache";
import ApplicationAccessPermissionControl from "./ApplicationAccessPermissionControl";
import Modal from "./Modal";
import IntegrationsSettings from "./IntegrationsSettings";
import {
  BrandConnection,
  BrandConnectionAccessLevel,
  BrandRecord,
  BrandUser,
} from "./brandPrototypeModel";
import {
  getBrandIcon,
  getBrandIconImageClassName,
} from "./integrations/IntegrationCard";
import type { BrandResource } from "./brandGroupViewModel";
import { formatConnectionMetaLine } from "./integrations/connectionMeta";
import {
  canUseMultipleBusinessGroups,
  isSingleBusinessGroupPlan,
  resolveBrandGroupAccessState,
  type BrandGroupAccessState,
} from "./brandGroupEntitlement";
import { getBrandConnectionOwnerDisplayName } from "./brandConnectionOwnership";
import {
  getBrandConnectionDisplayName,
  getBusinessGroupDisplayName,
} from "./brandGroupViewModel";
import {
  buildProjectAuthorizationConnections,
  buildInviteProjectAccessGrants,
  getDelegatableProjectConnectionAccessLevels,
  getInviteProjectAccessDraftKey,
  isProjectConnectionOwner,
  normalizeDelegatableProjectConnectionAccessLevel,
  normalizeProjectConnectionAccessLevel,
  type AssignableBrandConnection,
} from "./brandAssignableConnections";
import { isOwnerRoleOption } from "./roleAccessGuards";
import {
  canAddExistingWorkspaceIntegrations,
  canManageBusinessGroup,
  canManageBusinessGroupMembers,
  getManageableBusinessGroups,
  getBrandProjectActions,
} from "./brandProjectActions";
import { hasDuplicateBusinessGroupName } from "./businessGroupNameValidation";
import { resolveProjectOwnerAccess } from "./projectResourceAccess";
import { getProjectCreateHttpStatus } from "./projectCreatePermission";
import {
  canOfferBusinessGroupWorkspaceInvite,
  resolveBusinessGroupApplicationAccessActionAvailability,
  resolveBusinessGroupMemberAccessConnection,
  resolveBusinessGroupMemberAccessActionAvailability,
  type BusinessGroupMemberAccessTarget,
} from "./businessGroupApplicationAccess";
import { getBusinessGroupCreationFollowUp } from "./businessGroupCreationFollowUp";
import {
  hasHigherSelfServiceMemberTier,
  resolveEffectiveInviteMemberLimit,
  resolveInvitePlanCode,
  resolveInvitePlanName,
  resolveMemberInviteEligibility,
} from "./memberInviteAccess";
import {
  getMemberInviteLimitPrompt,
  getMemberInviteUpgradePrompt,
} from "./memberInviteUpgradePrompt";
import {
  MemberInviteLimitModal,
  MemberInviteUpgradeModal,
} from "./MemberInviteGateModals";
import DirectoryTooltipContent, {
  directoryTooltipContentClassName,
} from "./DirectoryTooltipContent";

export type BrandsSettingsDetailTab =
  | "overview"
  | "integrations"
  | "members"
  | "settings";
export type BrandsSettingsPageState =
  | { kind: "directory" }
  | { kind: "detail"; brandId: string; tab: BrandsSettingsDetailTab };
type BrandDialogState =
  | { kind: "closed" }
  | { kind: "create-brand" }
  | { kind: "edit-brand"; brandId: string }
  | { kind: "brand-limit" }
  | {
      kind: "grant-access";
      userId?: string;
      mode?: "workspace-member" | "invite-new-member";
    }
  | { kind: "batch-grant-access"; connectionId: string }
  | { kind: "remove-connection"; connectionId: string }
  | { kind: "delete-brand"; brandId?: string };
type BrandDialogSubmission =
  | "idle"
  | "creating-brand"
  | "adding-business-group-member"
  | "inviting-business-group-member";
type InviteRoleLoadState = "idle" | "loading" | "ready" | "error";
type BrandSettingsDraft = {
  brandId: string;
  name: string;
  domain: string;
  market: string;
  description: string;
  logoUrl: string | null;
};

const createBrandSettingsDraft = (brand: BrandRecord): BrandSettingsDraft => ({
  brandId: brand.id,
  name: brand.name,
  domain: brand.domain,
  market: brand.market || "",
  description: brand.description,
  logoUrl: brand.logoUrl || null,
});

type BrandAccessUser = BrandUser & {
  connectionAccess: Array<{
    connection: AssignableBrandConnection;
    accessLevel: Exclude<BrandConnectionAccessLevel, "no_access">;
  }>;
};

const BRAND_TABS: Array<{
  id: BrandsSettingsDetailTab;
  icon: typeof Storefront;
}> = [
  { id: "integrations", icon: SquaresFour },
  { id: "members", icon: Users },
];

const CONNECTION_ACCESS_LEVELS: BrandConnectionAccessLevel[] = [
  "no_access",
  "read",
  "action",
];
const BRAND_CREATED_TOAST_DURATION_MS = 8000;
const DIRECTORY_PREVIEW_LIMIT = 3;
const BUSINESS_GROUP_ACCESS_DROPDOWN_CLASSNAME =
  "integrations-apps-scroll-area z-[10050] max-h-[min(320px,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto overscroll-contain rounded-xl border border-[#E8EBF3] bg-white p-2 shadow-[0_16px_40px_rgba(26,22,47,0.12)] [scrollbar-gutter:stable] animate-in fade-in zoom-in-95 duration-100";

const isSecondaryProjectResource = (resource?: BrandResource) => {
  const type = resource?.resourceType?.trim().toUpperCase();
  return type === "CUSTOMER" || type === "PROPERTY" || type === "SITE";
};

/**
 * Hierarchical providers are authorized by their exact Project resource.
 * Other providers keep the connection-level grant identity for compatibility.
 */
const projectGrantIdentity = (
  connection: AssignableBrandConnection,
  userId: string,
) => {
  const connectionId = String(
    connection.integrationConnectionId || connection.id,
  );
  const resource = connection.resource;
  if (isSecondaryProjectResource(resource)) {
    return `${connectionId}:${resource?.resourceType?.trim().toUpperCase()}:${resource?.resourceKey}:${userId}`;
  }
  return `${connectionId}:CONNECTION:${connectionId}:${userId}`;
};

const formatDate = (value: string, locale: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(locale === "zh-CN" ? "zh-CN" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
};

const getConnectionDisplayName = ({
  provider,
  accountName,
  accountDetail,
}: {
  provider: string;
  accountName: string;
  accountDetail: string;
}) => getBrandConnectionDisplayName({ provider, accountName, accountDetail });

const ApplicationLogo = ({
  application,
  size = "h-5 w-5",
}: {
  application: ReturnType<typeof getIntegrationBrandIcon>;
  size?: string;
}) => (
  <img
    src={application.src || getBrandIcon(application.name)}
    alt={`${application.name} logo`}
    className={getBrandIconImageClassName(application.name, size)}
    onError={(event) => {
      event.currentTarget.src = "https://cdn.brandfetch.io/google.com/icon";
    }}
  />
);

const ApplicationConnectionSelect = ({
  value,
  options,
  onChange,
  ariaLabel,
  getConnectionOwnerLabel,
  disabled = false,
  readOnly = false,
}: {
  value: string;
  options: AssignableBrandConnection[];
  onChange: (connectionId: string) => void;
  ariaLabel: string;
  getConnectionOwnerLabel: (connection: AssignableBrandConnection) => string;
  disabled?: boolean;
  readOnly?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const selectedConnection = options.find(
    (connection) => connection.id === value,
  );
  const selectedApplication = selectedConnection
    ? getIntegrationBrandIcon(selectedConnection.provider)
    : null;
  const selectedAccount = selectedConnection
    ? getConnectionDisplayName(selectedConnection)
    : "";

  if (readOnly && selectedConnection && selectedApplication) {
    return (
      <div
        aria-label={ariaLabel}
        className="flex min-h-[72px] w-full items-center rounded-[10px] border border-[#E8EBF3] bg-[#F8F9FC] px-4"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-[#E8EBF3] bg-white p-1 shadow-xs">
            <ApplicationLogo application={selectedApplication} size="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-[#1A162F]">
              {selectedApplication.name}
            </span>
            {selectedAccount && selectedAccount !== selectedApplication.name ? (
              <span className="block truncate text-xs text-[#8A93B2]">
                {selectedAccount}
              </span>
            ) : null}
            <span className="mt-0.5 block truncate text-[11px] text-[#8A93B2]">
              {getConnectionOwnerLabel(selectedConnection)}
            </span>
          </span>
        </span>
      </div>
    );
  }

  return (
    <DropdownMenu.Root
      open={disabled ? false : open}
      onOpenChange={disabled ? undefined : setOpen}
    >
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className="flex h-[72px] w-full items-center justify-between gap-4 rounded-[10px] border border-[#C8D0E2] bg-white px-4 text-left outline-none transition-colors hover:border-[#98A2C0] focus-visible:border-[#1A162F] focus-visible:ring-2 focus-visible:ring-[#1A162F]/15 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {selectedConnection && selectedApplication ? (
            <span className="flex min-w-0 items-center gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-[#E8EBF3] bg-white p-1 shadow-xs">
                <ApplicationLogo
                  application={selectedApplication}
                  size="h-5 w-5"
                />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-[#1A162F]">
                  {selectedApplication.name}
                </span>
                {selectedAccount &&
                selectedAccount !== selectedApplication.name ? (
                  <span className="block truncate text-xs text-[#8A93B2]">
                    {selectedAccount}
                  </span>
                ) : null}
                <span className="mt-0.5 block truncate text-[11px] text-[#8A93B2]">
                  {getConnectionOwnerLabel(selectedConnection)}
                </span>
              </span>
            </span>
          ) : null}
          <CaretDown
            size={14}
            weight="regular"
            className={clsx(
              "shrink-0 text-[#7C86A6] transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className={BUSINESS_GROUP_ACCESS_DROPDOWN_CLASSNAME}
          style={{
            width: "var(--radix-dropdown-menu-trigger-width)",
            minWidth: "min(320px, calc(100vw - 16px))",
            maxWidth: "calc(100vw - 16px)",
          }}
        >
          {options.map((connection) => {
            const application = getIntegrationBrandIcon(connection.provider);
            const account = getConnectionDisplayName(connection);
            const connectionOwnerLabel = getConnectionOwnerLabel(connection);
            const isSelected = connection.id === value;

            return (
              <DropdownMenu.Item
                key={connection.id}
                onSelect={() => {
                  onChange(connection.id);
                  setOpen(false);
                }}
                className={clsx(
                  "flex min-w-0 items-center justify-between gap-3 rounded-[10px] px-3 py-2.5 text-left outline-none transition-colors data-[highlighted]:bg-[#F4F5F8] data-[highlighted]:text-[#1A162F]",
                  isSelected
                    ? "bg-[#1A162F]/5 text-[#1A162F]"
                    : "text-[#3D394D]",
                )}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-[#E8EBF3] bg-white p-1 shadow-xs">
                    <ApplicationLogo application={application} size="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">
                      {application.name}
                    </span>
                    {account && account !== application.name ? (
                      <span className="mt-0.5 block truncate text-xs text-[#8A93B2]">
                        {account}
                      </span>
                    ) : null}
                    <span className="mt-0.5 block truncate text-[11px] text-[#8A93B2]">
                      {connectionOwnerLabel}
                    </span>
                  </span>
                </span>
                {isSelected ? (
                  <Check
                    size={15}
                    weight="regular"
                    className="shrink-0 text-[#1A162F]"
                  />
                ) : null}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

const BrandAvatar = ({
  brand,
  size = "large",
}: {
  brand: BrandRecord;
  size?: "small" | "large";
}) => (
  <div
    className={clsx(
      "flex shrink-0 items-center justify-center overflow-hidden rounded-[10px]",
      brand.logoUrl
        ? "font-medium text-white"
        : "border border-[#E2E6F2] bg-[#F4F5F8] text-[#1A162F] shadow-xs",
      size === "large" ? "h-12 w-12 text-base" : "h-9 w-9 text-sm",
    )}
    style={brand.logoUrl ? { backgroundColor: brand.accent } : undefined}
    aria-hidden="true"
  >
    {brand.logoUrl ? (
      <img
        src={brand.logoUrl}
        alt=""
        className="h-full w-full object-contain bg-white p-0.5"
      />
    ) : (
      <Briefcase size={size === "large" ? 20 : 16} weight="bold" />
    )}
  </div>
);

const getConnectionPreviewItems = (connections: BrandConnection[]) =>
  Array.from(
    new Map(
      connections.map((connection) => [connection.provider, connection]),
    ).values(),
  ).slice(0, DIRECTORY_PREVIEW_LIMIT);

const BrandCardConnectionPreview = ({
  connections,
  countLabel,
  title,
}: {
  connections: BrandConnection[];
  countLabel: string;
  title: string;
}) => {
  const previewConnections = getConnectionPreviewItems(connections);

  if (connections.length === 0) {
    return (
      <span
        aria-label={countLabel}
        className="inline-flex min-w-0 items-center gap-1.5"
      >
        <SquaresFour
          aria-hidden="true"
          size={14}
          weight="regular"
          className="shrink-0 text-[#8A93B2]"
        />
        <span className="whitespace-nowrap">{countLabel}</span>
      </span>
    );
  }

  return (
    <Tooltip
      content={
        <DirectoryTooltipContent title={title}>
          {connections.map((connection) => {
            const application = getIntegrationBrandIcon(connection.provider);
            return (
              <li key={connection.id} className="flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#E8EBF3] bg-white p-1 shadow-xs">
                  <ApplicationLogo application={application} size="h-4 w-4" />
                </span>
                <span className="shrink-0 text-xs font-medium text-[#1A162F]">
                  {application.name}
                </span>
                {getConnectionDisplayName(connection) &&
                getConnectionDisplayName(connection) !== application.name ? (
                  <>
                    <span
                      className="h-1 w-1 shrink-0 rounded-full bg-[#94A3B8]"
                      aria-hidden="true"
                    />
                    <span className="min-w-0 truncate text-xs text-[#666F8D]">
                      {getConnectionDisplayName(connection)}
                    </span>
                  </>
                ) : null}
              </li>
            );
          })}
        </DirectoryTooltipContent>
      }
      side="top"
      align="start"
      contentClassName={directoryTooltipContentClassName}
      arrowClassName="!fill-white"
    >
      <span
        aria-label={countLabel}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex min-w-0 items-center gap-1.5 cursor-default"
      >
        <span className="flex shrink-0 -space-x-1.5" aria-hidden="true">
          {previewConnections.map((connection, index) => {
            const application = getIntegrationBrandIcon(connection.provider);
            return (
              <span
                key={connection.provider}
                className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full border-2 border-[#FCFCFC] bg-white"
                style={{ zIndex: DIRECTORY_PREVIEW_LIMIT - index }}
              >
                <ApplicationLogo application={application} size="h-5 w-5" />
              </span>
            );
          })}
        </span>
        <span className="whitespace-nowrap">{countLabel}</span>
      </span>
    </Tooltip>
  );
};

const BrandCardMemberPreview = ({
  members,
  countLabel,
  title,
}: {
  members: BrandUser[];
  countLabel: string;
  title: string;
}) => {
  const previewMembers = members.slice(0, DIRECTORY_PREVIEW_LIMIT);

  if (members.length === 0) {
    return (
      <span
        aria-label={countLabel}
        className="inline-flex min-w-0 items-center justify-end gap-1.5"
      >
        <Users
          aria-hidden="true"
          size={14}
          className="shrink-0 text-[#8A93B2]"
        />
        <span className="whitespace-nowrap">{countLabel}</span>
      </span>
    );
  }

  return (
    <Tooltip
      content={
        <DirectoryTooltipContent title={title}>
          {members.map((member) => (
            <li key={member.id} className="flex items-center gap-2">
              <img
                src={getMemberAvatarUrl(member)}
                alt=""
                className="h-5 w-5 shrink-0 rounded-full border border-[#E8EBF3] bg-white object-cover"
              />
              <span className="min-w-0 truncate text-xs font-medium text-[#1A162F]">
                {member.name}
              </span>
              {member.email ? (
                <>
                  <span
                    className="h-1 w-1 shrink-0 rounded-full bg-[#94A3B8]"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 truncate text-[11px] text-[#8A93B2]">
                    {member.email}
                  </span>
                </>
              ) : null}
            </li>
          ))}
        </DirectoryTooltipContent>
      }
      side="top"
      align="end"
      contentClassName={directoryTooltipContentClassName}
      arrowClassName="!fill-white"
    >
      <span
        aria-label={countLabel}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex min-w-0 items-center justify-end gap-1.5 cursor-default"
      >
        <span className="flex shrink-0 -space-x-1.5" aria-hidden="true">
          {previewMembers.map((member, index) => (
            <img
              key={member.id}
              src={getMemberAvatarUrl(member)}
              alt=""
              className="h-5 w-5 rounded-full border-2 border-[#FCFCFC] bg-white object-cover"
              style={{ zIndex: DIRECTORY_PREVIEW_LIMIT - index }}
            />
          ))}
        </span>
        <span className="whitespace-nowrap">{countLabel}</span>
      </span>
    </Tooltip>
  );
};

const Surface = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <section
    className={clsx(
      "rounded-2xl border border-[#E8EBF3] bg-[#FCFCFC]",
      className,
    )}
  >
    {children}
  </section>
);

const PrimaryButton = ({
  children,
  onClick,
  disabled = false,
  isLoading = false,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  title?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || isLoading}
    title={title}
    className="inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[10px] bg-[#1A162F] px-4 text-sm font-medium text-white transition-colors hover:bg-[#302A4B] disabled:cursor-not-allowed disabled:bg-[#C8D0E2]"
  >
    {isLoading ? (
      <CircleNotch size={16} weight="bold" className="animate-spin" />
    ) : (
      children
    )}
  </button>
);

const ProjectCreatePermissionTooltip = ({
  blocked,
  content,
  children,
  className,
}: {
  blocked: boolean;
  content: string;
  children: React.ReactNode;
  className?: string;
}) => {
  if (!blocked) {
    return className ? (
      <div className={className}>{children}</div>
    ) : (
      <>{children}</>
    );
  }

  return (
    <Tooltip content={content} side="top">
      <span
        tabIndex={0}
        aria-label={content}
        className={clsx(
          "inline-flex cursor-not-allowed [&>button]:pointer-events-none",
          className,
        )}
      >
        {children}
      </span>
    </Tooltip>
  );
};

const DangerButton = ({
  children,
  onClick,
  disabled = false,
  isLoading = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || isLoading}
    className="inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-[#B42318] px-4 text-sm font-medium text-white transition-colors hover:bg-[#8F1D14] disabled:cursor-not-allowed disabled:bg-[#E6B7B3]"
  >
    {isLoading ? (
      <CircleNotch size={16} weight="bold" className="animate-spin" />
    ) : (
      children
    )}
  </button>
);

const SecondaryButton = ({
  children,
  onClick,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex h-10 items-center justify-center gap-2 rounded-[10px] border border-[#C8D0E2] bg-white px-4 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:text-[#8A93B2]"
  >
    {children}
  </button>
);

const BrandGroupAccessPanel = ({
  access,
  title,
  description,
  actionLabel,
  permissionHint,
  statusLabel,
  onAction,
  fullHeight = false,
}: {
  access: BrandGroupAccessState;
  title: string;
  description: string;
  actionLabel: string;
  permissionHint?: string;
  statusLabel?: string;
  onAction: () => void;
  fullHeight?: boolean;
}) => (
  <Surface
    className={clsx(
      "flex items-center justify-center px-6 py-10 text-center",
      fullHeight && "min-h-[440px]",
    )}
  >
    <div className="max-w-[560px]">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#F4F5F8] text-[#1A162F]">
        <Briefcase size={22} weight="regular" />
      </span>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        <h2 className="text-lg font-medium text-[#1A162F]">{title}</h2>
        {access.isReadOnly && access.currentCount > 0 && statusLabel ? (
          <span className="rounded-full bg-[#F4F5F8] px-2.5 py-1 text-xs font-medium text-[#666F8D]">
            {statusLabel}
          </span>
        ) : null}
      </div>
      <p className="mt-2 text-sm leading-6 text-[#666F8D]">{description}</p>
      <div className="mt-5 flex justify-center">
        <PrimaryButton onClick={onAction}>{actionLabel}</PrimaryButton>
      </div>
      {permissionHint ? (
        <p className="mt-3 text-xs leading-5 text-[#8A93B2]">
          {permissionHint}
        </p>
      ) : null}
    </div>
  </Surface>
);

const BrandGroupLoadingSkeleton = ({
  detail,
  label,
}: {
  detail: boolean;
  label: string;
}) => (
  <div className="animate-pulse space-y-5" aria-busy="true" aria-label={label}>
    {detail ? (
      <>
        <div className="h-4 w-28 rounded bg-[#E8EBF3]" />
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded-xl bg-[#E8EBF3]" />
          <div className="space-y-2">
            <div className="h-7 w-56 rounded bg-[#E8EBF3]" />
            <div className="h-4 w-36 rounded bg-[#EEF0F5]" />
          </div>
        </div>
        <div className="flex gap-7 border-b border-[#E8EBF3] pb-2.5">
          <div className="h-5 w-12 rounded bg-[#E8EBF3]" />
          <div className="h-5 w-16 rounded bg-[#E8EBF3]" />
        </div>
        <Surface className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-[#E8EBF3] px-5 py-5">
            <div className="h-4 w-64 rounded bg-[#E8EBF3]" />
            <div className="h-10 w-40 rounded-[10px] bg-[#E8EBF3]" />
          </div>
          <div className="space-y-4 px-5 py-5">
            <div className="h-12 rounded-lg bg-[#F4F5F8]" />
            <div className="h-12 rounded-lg bg-[#F4F5F8]" />
            <div className="h-12 rounded-lg bg-[#F4F5F8]" />
          </div>
        </Surface>
      </>
    ) : (
      <>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="h-7 w-32 rounded bg-[#E8EBF3]" />
            <div className="h-4 w-96 max-w-full rounded bg-[#EEF0F5]" />
          </div>
          <div className="h-10 w-36 rounded-[10px] bg-[#E8EBF3]" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            <div className="h-9 w-28 rounded-full bg-[#E8EBF3]" />
            <div className="h-9 w-24 rounded-full bg-[#EEF0F5]" />
          </div>
          <div className="h-10 w-44 rounded-[10px] bg-[#E8EBF3]" />
        </div>
        <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,340px),1fr))]">
          {[0, 1, 2].map((index) => (
            <Surface key={index} className="h-[176px] p-4">
              <div className="flex items-center gap-2.5">
                <div className="h-10 w-10 rounded-xl bg-[#E8EBF3]" />
                <div className="space-y-2">
                  <div className="h-4 w-36 rounded bg-[#E8EBF3]" />
                  <div className="h-3 w-24 rounded bg-[#EEF0F5]" />
                </div>
              </div>
              <div className="mt-5 h-px bg-[#E8EBF3]" />
              <div className="mt-4 flex justify-between">
                <div className="h-5 w-24 rounded bg-[#EEF0F5]" />
                <div className="h-5 w-20 rounded bg-[#EEF0F5]" />
              </div>
            </Surface>
          ))}
        </div>
      </>
    )}
  </div>
);

const WorkspaceUserSelect = ({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder,
  disabledLabel,
  inviteNewMemberLabel,
  onInviteNewMember,
}: {
  value: string;
  options: Array<{
    id: string;
    name: string;
    email: string;
    logoUrl?: string;
    disabled?: boolean;
  }>;
  onChange: (userId: string) => void;
  ariaLabel: string;
  placeholder: string;
  disabledLabel?: string;
  inviteNewMemberLabel?: string;
  onInviteNewMember?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const selectedUser = options.find((option) => option.id === value);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className="flex h-14 w-full items-center justify-between gap-4 rounded-[10px] border border-[#C8D0E2] bg-white px-4 text-left outline-none transition-colors hover:border-[#98A2C0] focus-visible:border-[#1A162F] focus-visible:ring-2 focus-visible:ring-[#1A162F]/15"
        >
          {selectedUser ? (
            <span className="flex min-w-0 items-center gap-3">
              <img
                src={getMemberAvatarUrl(selectedUser)}
                alt=""
                className="h-8 w-8 shrink-0 rounded-full border border-gray-200 bg-white object-cover"
              />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-[#1A162F]">
                  {selectedUser.name}
                </span>
                <span className="block truncate text-xs text-[#8A93B2]">
                  {selectedUser.email}
                </span>
              </span>
            </span>
          ) : (
            <span className="truncate text-sm text-[#8A93B2]">
              {placeholder}
            </span>
          )}
          <CaretDown
            size={14}
            weight="regular"
            className={clsx(
              "shrink-0 text-[#7C86A6] transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className={BUSINESS_GROUP_ACCESS_DROPDOWN_CLASSNAME}
          style={{
            width: "var(--radix-dropdown-menu-trigger-width)",
            minWidth: "min(320px, calc(100vw - 16px))",
            maxWidth: "calc(100vw - 16px)",
          }}
        >
          {options.map((option) => {
            const isSelected = option.id === value;
            return (
              <DropdownMenu.Item
                key={option.id}
                disabled={option.disabled}
                onSelect={() => {
                  if (option.disabled) {
                    return;
                  }
                  onChange(option.id);
                  setOpen(false);
                }}
                className={clsx(
                  "flex min-w-0 items-center justify-between gap-3 rounded-[10px] px-3 py-3 text-left outline-none transition-colors data-[highlighted]:bg-[#F4F5F8] data-[highlighted]:text-[#1A162F]",
                  isSelected
                    ? "bg-[#1A162F]/5 text-[#1A162F]"
                    : "text-[#3D394D]",
                  option.disabled
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer",
                )}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <img
                    src={getMemberAvatarUrl(option)}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded-full border border-gray-200 bg-white object-cover"
                  />
                  <span className="min-w-0">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-sm font-medium">
                        {option.name}
                      </span>
                      {option.disabled && disabledLabel ? (
                        <span className="shrink-0 rounded-full bg-[#EEF0F5] px-1.5 py-0.5 text-[10px] font-medium text-[#667085]">
                          {disabledLabel}
                        </span>
                      ) : null}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-[#8A93B2]">
                      {option.email}
                    </span>
                  </span>
                </span>
                {isSelected ? (
                  <Check
                    size={15}
                    weight="regular"
                    className="shrink-0 text-[#1A162F]"
                  />
                ) : null}
              </DropdownMenu.Item>
            );
          })}
          {onInviteNewMember && inviteNewMemberLabel ? (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-[#E8EBF3]" />
              <DropdownMenu.Item
                onSelect={() => {
                  setOpen(false);
                  onInviteNewMember();
                }}
                className="flex cursor-pointer items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-sm font-normal text-[#667085] outline-none transition-colors data-[highlighted]:bg-[#F8F9FC] data-[highlighted]:text-[#1A162F]"
              >
                <UserCirclePlus
                  aria-hidden="true"
                  size={18}
                  weight="regular"
                  className="shrink-0"
                />
                <span>{inviteNewMemberLabel}</span>
              </DropdownMenu.Item>
            </>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

interface BrandsSettingsProps {
  initialBrandId?: string | null;
  initialTab?: BrandsSettingsDetailTab;
  onPageChange?: (page: BrandsSettingsPageState) => void;
}

const BrandsSettings = ({
  initialBrandId = null,
  initialTab = "overview",
  onPageChange,
}: BrandsSettingsProps = {}) => {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const {
    user,
    permissions,
    integrations,
    workspaceMembers,
    userBilling,
    currentWorkspace,
  } = useAuth();
  const { modal } = useAdminContext();
  const { showToast, showActionToast } = useToast();
  const {
    feature: brandFeature,
    data: brandGroupData,
    isLoading: brandGroupLoading,
    error: brandGroupError,
    createBrand: createProject,
    updateBrand: updateProject,
    deleteBrand: deleteProject,
    assignResourceToBrand: assignProjectResource,
    removeResourceFromBrand: removeProjectResource,
    assignIntegrationToBrand: assignProjectIntegration,
    removeIntegrationFromBrand: removeProjectIntegration,
    refresh: refreshBrandGroupFromContext,
  } = useBrandGroup();
  const data = brandGroupData;
  const [page, setPage] = useState<BrandsSettingsPageState>(() =>
    initialBrandId
      ? { kind: "detail", brandId: initialBrandId, tab: initialTab }
      : { kind: "directory" },
  );
  useEffect(() => {
    setPage(
      initialBrandId
        ? { kind: "detail", brandId: initialBrandId, tab: initialTab }
        : { kind: "directory" },
    );
  }, [initialBrandId, initialTab]);
  const changePage = (nextPage: BrandsSettingsPageState) => {
    setPage(nextPage);
    onPageChange?.(nextPage);
  };
  const [dialog, setDialog] = useState<BrandDialogState>({ kind: "closed" });
  const [isInviteUpgradeModalOpen, setIsInviteUpgradeModalOpen] =
    useState(false);
  const [isInviteLimitModalOpen, setIsInviteLimitModalOpen] = useState(false);
  const [isInviteLimitContactSalesOpen, setIsInviteLimitContactSalesOpen] =
    useState(false);
  const [dialogSubmission, setDialogSubmission] =
    useState<BrandDialogSubmission>("idle");
  const [isSavingProjectAction, setIsSavingProjectAction] = useState(false);
  // A dialog can receive a second click before React disables its primary
  // button. Keep the full dialog submission lifecycle in one synchronous
  // state machine so late open events cannot remount a dialog during a save.
  const dialogSubmissionRef = useRef<BrandDialogSubmission>("idle");
  const beginDialogSubmission = useCallback(
    (submission: Exclude<BrandDialogSubmission, "idle">) => {
      if (dialogSubmissionRef.current !== "idle") {
        return false;
      }
      dialogSubmissionRef.current = submission;
      setDialogSubmission(submission);
      return true;
    },
    [],
  );
  const finishDialogSubmission = useCallback(
    (submission: Exclude<BrandDialogSubmission, "idle">) => {
      if (dialogSubmissionRef.current !== submission) {
        return;
      }
      dialogSubmissionRef.current = "idle";
      setDialogSubmission("idle");
    },
    [],
  );
  const isCreatingBrand = dialogSubmission === "creating-brand";
  const isAddingBusinessGroupMember =
    dialogSubmission === "adding-business-group-member";
  const isInvitingBusinessGroupMember =
    dialogSubmission === "inviting-business-group-member";
  const [directoryScopeFilter, setDirectoryScopeFilter] = useState<
    "workspace" | "private"
  >("workspace");
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const canCreateProject =
    permissions.status === "ready" &&
    permissions.checkPermissions([Permission.BRAND_MANAGE_ACCESS]);
  const canCreateWorkspaceProject = permissions.isOwner || canCreateProject;
  const projectCreatePermissionDenied =
    permissions.status === "ready" && !canCreateProject;
  const projectCreatePermissionMessage = t(
    "website.brandsSettings.simple.projectCreateForbidden",
  );
  const [brandScope, setBrandScope] = useState<"workspace" | "private">(
    canCreateWorkspaceProject ? "workspace" : "private",
  );
  const [brandName, setBrandName] = useState("");
  const [brandDomain, setBrandDomain] = useState("");
  const [brandMarket, setBrandMarket] = useState("");
  const [brandDescription, setBrandDescription] = useState("");
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);
  const [memberToAdd, setMemberToAdd] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteEmailError, setInviteEmailError] = useState("");
  const [inviteRoleId, setInviteRoleId] = useState("");
  const [inviteRoleOptions, setInviteRoleOptions] = useState<
    WorkspaceDataRole[]
  >([]);
  const [inviteRoleLoadState, setInviteRoleLoadState] =
    useState<InviteRoleLoadState>("idle");
  const [batchMemberAccessDraft, setBatchMemberAccessDraft] = useState<
    Record<string, BrandConnectionAccessLevel>
  >({});
  const [connectionAccessDraft, setConnectionAccessDraft] = useState<
    Record<string, BrandConnectionAccessLevel>
  >({});
  const [settingsDraft, setSettingsDraft] = useState<BrandSettingsDraft | null>(
    null,
  );
  const logoInputRef = useRef<HTMLInputElement>(null);
  const createLogoInputRef = useRef<HTMLInputElement>(null);
  const [isLogoReading, setIsLogoReading] = useState(false);
  const brandGroupPlanCode =
    userBilling.summary?.planCode ||
    currentWorkspace?.subscription?.plan_code ||
    userBilling.userPlan?.planCode;
  const brandGroupSubscriptionStatus =
    userBilling.summary?.status || userBilling.userPlan?.status;
  const brandGroupAccess = useMemo(
    () =>
      resolveBrandGroupAccessState({
        planCode: brandGroupPlanCode,
        subscriptionStatus: brandGroupSubscriptionStatus,
        isExpired: userBilling.isExpired,
        isTrial: userBilling.isTrial,
        isLoading: userBilling.loading.plan,
        currentCount: data.totalBrandCount,
        featureLoaded: brandFeature !== null,
        featureEntitled: brandFeature?.planEntitled,
        featureMaxBrands: brandFeature?.maxBrands,
      }),
    [
      brandGroupPlanCode,
      brandGroupSubscriptionStatus,
      data.totalBrandCount,
      userBilling.isExpired,
      userBilling.isTrial,
      userBilling.loading.plan,
      brandFeature,
    ],
  );
  const brandFeatureInitializing =
    brandFeature?.state === "ENABLING" ||
    (brandFeature?.planEntitled === true &&
      brandFeature.brandUiEnabled !== true);
  const shouldShowBrandGroupLoadingSkeleton =
    brandGroupLoading || brandGroupAccess.kind === "loading";
  const canUseMultipleBusinessGroupsInWorkspace = canUseMultipleBusinessGroups(
    brandGroupAccess.limit,
  );
  const canManageBilling =
    permissions.isOwner ||
    permissions.checkPermissions([Permission.SUBSCRIPTION_MANAGE]);
  const adminProductFeatures = getAdminProductFeatures();
  const canInviteWorkspaceMembers = permissions.checkPermissions([
    Permission.MEMBER_INVITE,
  ]);

  const workspaceUsers = useMemo<BrandUser[]>(
    () =>
      workspaceMembers.members
        .filter((member) => member.status === "ACTIVE")
        .map((member) => {
          const workspaceRole = String(
            member.workspaceRole || member.membershipRole || member.role,
          ).toUpperCase();

          return {
            id: member.userId,
            name: member.displayName || member.name || member.email,
            email: member.email,
            logoUrl: member.logoUrl,
            role:
              workspaceRole === "OWNER"
                ? "Owner"
                : workspaceRole === "ADMIN"
                  ? "Admin"
                  : "Member",
          };
        }),
    [workspaceMembers.members],
  );
  const workspaceInvitePlanCode = resolveInvitePlanCode({
    entitlementPlanCode: workspaceMembers.entitlements.memberLimit?.planCode,
    currentWorkspacePlanCode: currentWorkspace?.subscription?.plan_code,
    billingSummaryPlanCode: userBilling.summary?.planCode,
    userPlanCode: userBilling.userPlan?.planCode,
  });
  const workspaceInviteMemberLimit = resolveEffectiveInviteMemberLimit({
    planCode: workspaceInvitePlanCode,
    memberLimit: workspaceMembers.entitlements.memberLimit,
  });
  const workspaceMemberInviteEligibility = resolveMemberInviteEligibility({
    hasInvitePermission: canInviteWorkspaceMembers,
    memberInvitesEnabled: adminProductFeatures.memberInvites,
    planCode: workspaceInvitePlanCode,
    memberCount: workspaceUsers.length,
    memberLimit: workspaceInviteMemberLimit,
  });
  const workspaceInviteLimitPrimaryActionTarget =
    hasHigherSelfServiceMemberTier(workspaceInvitePlanCode)
      ? "plans"
      : "sales";
  const workspaceInviteUpgradePrompt = getMemberInviteUpgradePrompt(t);
  const workspaceInviteLimitPrompt = getMemberInviteLimitPrompt(t, {
    planName: resolveInvitePlanName(workspaceInvitePlanCode),
    memberLimit: workspaceInviteMemberLimit,
    primaryActionTarget: workspaceInviteLimitPrimaryActionTarget,
  });
  const brandConnections = useMemo<AssignableBrandConnection[]>(
    () => buildProjectAuthorizationConnections(brandGroupData),
    [brandGroupData],
  );
  const activeBrand =
    page.kind === "detail"
      ? data.brands.find((brand) => brand.id === page.brandId) || null
      : null;
  const isSingleBusinessGroupMode =
    data.brands.length === 1 &&
    isSingleBusinessGroupPlan({
      planCode: brandGroupPlanCode,
      featureMaxBrands: brandFeature?.maxBrands,
    });
  const isActiveBrandDefault = Boolean(activeBrand?.isDefault);
  const activeTab = page.kind === "detail" ? page.tab : null;
  const isBusinessGroupInviteMode =
    dialog.kind === "grant-access" &&
    !dialog.userId &&
    dialog.mode === "invite-new-member";
  useEffect(() => {
    if (!isBusinessGroupInviteMode || !currentWorkspace?.id) {
      setInviteRoleLoadState("idle");
      return;
    }

    let cancelled = false;
    setInviteRoleLoadState("loading");
    setInviteRoleOptions([]);
    setInviteRoleId("");
    void featureService
      .getWorkspaceDataRoles(currentWorkspace.id)
      .then((roles) => {
        if (cancelled) return;
        const availableRoles = roles.filter(
          (role) =>
            role.status !== "INACTIVE" &&
            !isOwnerRoleOption({
              id: String(role.id),
              name: role.name,
              roleKey: role.roleKey,
            }),
        );
        if (availableRoles.length === 0) {
          setInviteRoleLoadState("error");
          return;
        }
        const defaultRole =
          availableRoles.find((role) => role.defaultRole) ||
          availableRoles.find(
            (role) =>
              String(role.roleKey || role.name)
                .trim()
                .toUpperCase() === "MEMBER",
          ) ||
          availableRoles[0];
        setInviteRoleOptions(availableRoles);
        setInviteRoleId(String(defaultRole.id));
        setInviteRoleLoadState("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setInviteRoleLoadState("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id, isBusinessGroupInviteMode]);
  const filteredDirectoryBrands = useMemo(() => {
    return data.brands.filter((brand) => {
      const isPrivate = brand.scope === "private";
      if (directoryScopeFilter === "workspace" ? isPrivate : !isPrivate) {
        return false;
      }
      if (projectSearchQuery.trim()) {
        const q = projectSearchQuery.trim().toLowerCase();
        const matchName = brand.name.toLowerCase().includes(q);
        const matchDomain = brand.domain?.toLowerCase().includes(q);
        if (!matchName && !matchDomain) return false;
      }
      return true;
    });
  }, [data.brands, directoryScopeFilter, projectSearchQuery]);
  const shouldShowDirectoryCreateCard =
    !projectSearchQuery.trim() &&
    brandGroupAccess.canCreate &&
    canUseMultipleBusinessGroupsInWorkspace;
  const directoryScopeCounts = useMemo(
    () =>
      data.brands.reduce(
        (counts, brand) => {
          if (brand.scope === "private") {
            counts.private += 1;
          } else {
            counts.workspace += 1;
          }
          return counts;
        },
        { workspace: 0, private: 0 },
      ),
    [data.brands],
  );
  const projectCreatorCounts = useMemo(() => {
    const workspaceUsersById = new Map(
      workspaceUsers.map((workspaceUser) => [workspaceUser.id, workspaceUser]),
    );
    return data.projectUsageByCreator
      .map((usage) => {
        const creatorUser = workspaceUsersById.get(usage.creatorUserId);
        return {
          id: usage.creatorUserId,
          name:
            creatorUser?.name ||
            t("website.brandsSettings.simple.unknownProjectCreator"),
          workspaceCount: usage.workspaceCount,
          privateCount: usage.privateCount,
        };
      })
      .sort(
        (first, second) =>
          second.workspaceCount +
            second.privateCount -
            (first.workspaceCount + first.privateCount) ||
          first.name.localeCompare(second.name),
      );
  }, [data.projectUsageByCreator, t, workspaceUsers]);
  const activeConnections = useMemo(
    () =>
      activeBrand
        ? brandConnections.filter((connection) =>
            connection.brandIds.includes(activeBrand.id),
          )
        : [],
    [activeBrand, brandConnections],
  );
  // The Project resource contract and the grant matrix load independently.
  // Re-read grants after the assigned set changes while this page is open;
  // otherwise members keep the pre-assignment DISABLED snapshot.
  const activeConnectionGrantReloadKey = useMemo(
    () =>
      activeConnections
        .map(
          (connection) =>
            `${connection.id}:${connection.integrationConnectionId || ""}`,
        )
        .sort()
        .join("|"),
    [activeConnections],
  );
  // The Project grant API is the source of truth. The prototype context remains
  // only as a short-lived UI cache for the older settings components.
  const [projectConnectionGrants, setProjectConnectionGrants] = useState<
    Record<string, BrandConnectionAccessLevel>
  >({});
  const [
    hasLoadedProjectConnectionGrants,
    setHasLoadedProjectConnectionGrants,
  ] = useState(false);
  const [
    projectConnectionGrantsLoadError,
    setProjectConnectionGrantsLoadError,
  ] = useState(false);
  const projectBootstrapCachePrefix =
    user?.id && currentWorkspace?.id && activeBrand?.id
      ? `user:${user.id}:workspace:${currentWorkspace.id}:project:${activeBrand.id}`
      : null;
  const refreshBrandGroup = useCallback(async () => {
    if (projectBootstrapCachePrefix) {
      invalidateProjectIntegrationsBootstrapCache(projectBootstrapCachePrefix);
    }
    await refreshBrandGroupFromContext();
  }, [projectBootstrapCachePrefix, refreshBrandGroupFromContext]);
  const [projectIntegrationsBootstrap, setProjectIntegrationsBootstrap] =
    useState<ProjectIntegrationsBootstrapResponse | null>(null);
  const projectConnectionGrantsReady = hasLoadedProjectConnectionGrants;
  const projectIntegrationsBootstrapReady =
    projectIntegrationsBootstrap !== null || projectConnectionGrantsLoadError;
  useEffect(() => {
    if (
      (activeTab !== "integrations" && activeTab !== "members") ||
      !activeBrand ||
      !currentWorkspace?.id ||
      !user?.id ||
      !projectBootstrapCachePrefix
    ) {
      setProjectConnectionGrants({});
      setHasLoadedProjectConnectionGrants(false);
      setProjectConnectionGrantsLoadError(false);
      setProjectIntegrationsBootstrap(null);
      return;
    }
    let cancelled = false;
    setHasLoadedProjectConnectionGrants(false);
    setProjectConnectionGrantsLoadError(false);
    setProjectConnectionGrants({});
    setProjectIntegrationsBootstrap(null);
    const applyProjectResourceGrants = (grants: ProjectUserResourceGrant[]) => {
      const next: Record<string, BrandConnectionAccessLevel> = {};
      (grants || []).forEach((grant) => {
        next[
          `${grant.connectionId}:${grant.resourceType}:${grant.resourceKey}:${grant.userId}`
        ] =
          grant.accessLevel === "WRITE"
            ? "action"
            : grant.accessLevel === "READ"
              ? "read"
              : "no_access";
      });
      setProjectConnectionGrants(next);
    };
    const loadProjectResourceGrants =
      activeTab === "members"
        ? getProjectIntegrationsBootstrapCached<ProjectUserResourceGrant[]>(
            `${projectBootstrapCachePrefix}:grants:${activeConnectionGrantReloadKey}`,
            () =>
              projectService.listProjectResourceGrants(
                currentWorkspace.id,
                activeBrand.id,
              ),
          ).then((grants) => {
            if (cancelled) return;
            applyProjectResourceGrants(grants);
            setHasLoadedProjectConnectionGrants(true);
          })
        : getProjectIntegrationsBootstrapCached(
            `${projectBootstrapCachePrefix}:assignments:${activeConnectionGrantReloadKey}`,
            () =>
              projectService.getProjectIntegrationsBootstrap(
                currentWorkspace.id,
                activeBrand.id,
              ),
          ).then((bootstrap) => {
            if (cancelled) return;
            setProjectIntegrationsBootstrap(bootstrap);
            applyProjectResourceGrants(bootstrap.resourceGrants || []);
            setHasLoadedProjectConnectionGrants(true);
          });
    void loadProjectResourceGrants.catch(() => {
      if (!cancelled) {
        setProjectConnectionGrants({});
        setProjectIntegrationsBootstrap(null);
        setProjectConnectionGrantsLoadError(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    activeBrand?.id,
    activeTab,
    activeConnectionGrantReloadKey,
    currentWorkspace?.id,
    projectBootstrapCachePrefix,
    user?.id,
  ]);
  const resolveBrandUserConnectionAccess = useCallback(
    (
      connection: AssignableBrandConnection,
      userId: string,
    ): BrandConnectionAccessLevel => {
      if (!activeBrand) {
        return "no_access";
      }

      if (isProjectConnectionOwner(connection, userId)) {
        return "action";
      }

      const isProjectOwner = [
        activeBrand.createdByUserId,
        activeBrand.primaryOwnerUserId,
      ].some((ownerId) => ownerId && ownerId === userId);
      const ownerAccess = resolveProjectOwnerAccess(
        connection.brandAccessCeilings?.[activeBrand.id],
        false,
      );
      if (isProjectOwner && ownerAccess !== "no_access") {
        return ownerAccess;
      }

      const persistedAccess =
        projectConnectionGrants[projectGrantIdentity(connection, userId)];
      if (persistedAccess) {
        // Persisted grants are audit facts. The assignment ceiling can be
        // lowered or invalidated after the grant was written, so the member
        // table must display the same effective access that runtime enforces.
        return normalizeProjectConnectionAccessLevel(
          connection,
          activeBrand.id,
          userId,
          persistedAccess,
        );
      }
      // An absent row in the Project grant matrix means DISABLED. Never
      // resurrect a stale prototype/Workspace-level value while it loads.
      return "no_access";
    },
    [activeBrand, projectConnectionGrants],
  );
  const getConnectionAccessLevelsForCeiling = useCallback(
    (
      connection: AssignableBrandConnection,
      targetUserId?: string,
    ): BrandConnectionAccessLevel[] =>
      getDelegatableProjectConnectionAccessLevels(
        connection,
        activeBrand?.id,
        user?.id
          ? resolveBrandUserConnectionAccess(connection, user.id)
          : "no_access",
        targetUserId,
      ),
    [activeBrand?.id, resolveBrandUserConnectionAccess, user?.id],
  );
  const normalizeDraftAccessForConnection = useCallback(
    (
      connection: AssignableBrandConnection,
      accessLevel: BrandConnectionAccessLevel,
      targetUserId?: string,
    ): BrandConnectionAccessLevel =>
      normalizeDelegatableProjectConnectionAccessLevel(
        connection,
        activeBrand?.id,
        user?.id
          ? resolveBrandUserConnectionAccess(connection, user.id)
          : "no_access",
        targetUserId,
        accessLevel,
      ),
    [activeBrand?.id, resolveBrandUserConnectionAccess, user?.id],
  );
  const activeProjectCreatorId =
    activeBrand?.createdByUserId || activeBrand?.primaryOwnerUserId || "";
  const activeMemberIds = useMemo(
    () =>
      new Set(
        activeBrand
          ? data.memberships
              .filter((membership) => membership.brandId === activeBrand.id)
              .map((membership) => membership.userId)
          : [],
      ),
    [activeBrand, data.memberships],
  );
  const activeUsers = useMemo<BrandAccessUser[]>(() => {
    if (!activeBrand) {
      return [];
    }

    const visibleUsers = workspaceUsers.flatMap((workspaceUser) => {
      const connectionAccess = activeConnections.flatMap((connection) => {
        const accessLevel = resolveBrandUserConnectionAccess(
          connection,
          workspaceUser.id,
        );

        return accessLevel && accessLevel !== "no_access"
          ? [{ connection, accessLevel }]
          : [];
      });

      return isActiveBrandDefault ||
        activeMemberIds.has(workspaceUser.id) ||
        connectionAccess.length > 0
        ? [{ ...workspaceUser, connectionAccess }]
        : [];
    });

    return visibleUsers.sort(
      (first, second) =>
        Number(second.id === activeProjectCreatorId) -
        Number(first.id === activeProjectCreatorId),
    );
  }, [
    activeBrand,
    activeProjectCreatorId,
    activeConnections,
    activeMemberIds,
    isActiveBrandDefault,
    resolveBrandUserConnectionAccess,
    workspaceUsers,
  ]);
  const activeBusinessGroupMembers = useMemo<BrandAccessUser[]>(
    () =>
      isActiveBrandDefault
        ? activeUsers
        : activeUsers.filter((brandUser) => activeMemberIds.has(brandUser.id)),
    [activeMemberIds, activeUsers, isActiveBrandDefault],
  );
  const workspaceUserOptions = useMemo(
    () =>
      workspaceUsers
        .filter(
          (workspaceUser) =>
            workspaceUser.id !== user?.id &&
            workspaceUser.email !== user?.email,
        )
        .map((workspaceUser) => ({
          ...workspaceUser,
          disabled:
            !isActiveBrandDefault && activeMemberIds.has(workspaceUser.id),
        })),
    [
      activeMemberIds,
      isActiveBrandDefault,
      user?.email,
      user?.id,
      workspaceUsers,
    ],
  );
  const hasAssignableWorkspaceUser = workspaceUserOptions.some(
    (workspaceUser) => !workspaceUser.disabled,
  );
  const workspaceMembersLoadError =
    workspaceMembers.status === "error" ? workspaceMembers.error : null;
  const workspaceUserEmptyStateTitle =
    workspaceUserOptions.length === 0
      ? t("website.brandsSettings.simple.noWorkspaceUsersToAddTitle")
      : t("website.brandsSettings.simple.allWorkspaceUsersAddedTitle");
  const handleGoToWorkspaceUsers = useCallback(() => {
    setDialog({ kind: "closed" });
    navigate(buildAdminSettingsPath("users"));
  }, [navigate]);
  const handleGoToIntegrations = useCallback(() => {
    setDialog({ kind: "closed" });
    navigate(buildAdminSettingsPath("integrations"));
  }, [navigate]);
  const handleRetryWorkspaceUsers = useCallback(() => {
    void workspaceMembers.refresh().catch(() => undefined);
  }, [workspaceMembers.refresh]);
  const selectedAccessUser = useMemo(
    () =>
      workspaceUsers.find(
        (workspaceUser) => workspaceUser.id === memberToAdd,
      ) || null,
    [memberToAdd, workspaceUsers],
  );
  // Brand drafts created while auth is still hydrating may have been keyed by
  // the user's email. Keep both stable identity forms so that saving a Brand
  // never turns its creator into a read-only user once the user id arrives.
  const currentPrototypeUserIds = useMemo(
    () =>
      [
        workspaceUsers.find(
          (prototypeUser) =>
            prototypeUser.id === user?.id ||
            prototypeUser.email === user?.email,
        )?.id,
        user?.id,
        user?.email,
      ].filter((value): value is string => Boolean(value)),
    [user?.email, user?.id, workspaceUsers],
  );
  const currentPrototypeUserId = currentPrototypeUserIds[0] || "";
  const currentPrototypeUser =
    workspaceUsers.find((prototypeUser) =>
      currentPrototypeUserIds.includes(prototypeUser.id),
    ) || null;
  const isWorkspaceOwner =
    permissions.isOwner ||
    workspaceMembers.currentMembershipRole === "OWNER" ||
    currentPrototypeUser?.role === "Owner";
  const isProjectOwner = useCallback(
    (brand: BrandRecord) =>
      getManageableBusinessGroups([brand], currentPrototypeUserIds, false)
        .length > 0,
    [currentPrototypeUserIds],
  );
  const getProjectActions = useCallback(
    (brand: BrandRecord) =>
      getBrandProjectActions({
        isDefault: Boolean(brand.isDefault),
        isProjectOwner: isProjectOwner(brand),
        isWorkspaceOwner,
        isReadOnly: brandGroupAccess.isReadOnly,
      }),
    [brandGroupAccess.isReadOnly, isProjectOwner, isWorkspaceOwner],
  );
  const isProjectManager = useCallback(
    (brand: BrandRecord) =>
      canManageBusinessGroup({
        isWorkspaceOwner,
        isProjectOwner: isProjectOwner(brand),
        isReadOnly: brandGroupAccess.isReadOnly,
      }),
    [brandGroupAccess.isReadOnly, isProjectOwner, isWorkspaceOwner],
  );
  const isActiveProjectManager = Boolean(
    activeBrand && isProjectManager(activeBrand),
  );
  const isActiveProjectOwner = Boolean(
    activeBrand && isProjectOwner(activeBrand),
  );
  const targetBrandForEdit =
    dialog.kind === "edit-brand"
      ? data.brands.find((brand) => brand.id === dialog.brandId) || null
      : null;
  const targetBrandForDelete =
    dialog.kind === "delete-brand"
      ? (dialog.brandId
          ? data.brands.find((b) => b.id === dialog.brandId)
          : activeBrand) || activeBrand
      : null;
  const canManageActiveBrand = Boolean(
    !brandGroupAccess.isReadOnly && isActiveProjectManager,
  );
  const canManageActiveBrandMemberAccess = Boolean(
    !brandGroupAccess.isReadOnly &&
      (isActiveProjectOwner || isWorkspaceOwner),
  );
  const canAddExistingConnectionsToActiveBrand =
    canAddExistingWorkspaceIntegrations({
      isProjectOwner: isActiveProjectOwner,
      isWorkspaceOwner,
      isReadOnly: brandGroupAccess.isReadOnly,
    });
  const canManageConnectionAccess = useCallback(
    (_connection: AssignableBrandConnection, _targetUserId?: string) => {
      if (brandGroupAccess.isReadOnly || !activeBrand) {
        return false;
      }

      // Connection ownership governs only the Workspace OAuth lifecycle.
      // Business-group owners and Workspace Owners administer member grants.
      return canManageActiveBrandMemberAccess;
    },
    [activeBrand, brandGroupAccess.isReadOnly, canManageActiveBrandMemberAccess],
  );
  const getManageableActiveConnectionsForUser = useCallback(
    (targetUserId?: string) =>
      activeConnections.filter((connection) =>
        canManageConnectionAccess(connection, targetUserId),
      ),
    [activeConnections, canManageConnectionAccess],
  );
  const manageableActiveConnections = useMemo(
    () => getManageableActiveConnectionsForUser(),
    [getManageableActiveConnectionsForUser],
  );
  const editableActiveConnections = useMemo(
    () => getManageableActiveConnectionsForUser(memberToAdd || undefined),
    [getManageableActiveConnectionsForUser, memberToAdd],
  );
  const canInviteNewWorkspaceMemberToActiveBusinessGroup =
    canOfferBusinessGroupWorkspaceInvite({
      canInviteWorkspaceMembers:
        workspaceMemberInviteEligibility !== "permission-denied",
      assignableApplicationCount: manageableActiveConnections.length,
    });
  const workspaceUserEmptyStateActionLabel =
    canInviteNewWorkspaceMemberToActiveBusinessGroup
      ? t("common.inviteMembers")
      : t("website.brandsSettings.simple.goToWorkspaceUsers");
  const hasPositiveConnectionAccessDraft = useMemo(
    () =>
      Boolean(memberToAdd) &&
      editableActiveConnections.some(
        (connection) =>
          normalizeDraftAccessForConnection(
            connection,
            connectionAccessDraft[connection.id] || "no_access",
            memberToAdd,
          ) !== "no_access",
      ),
    [
      connectionAccessDraft,
      editableActiveConnections,
      memberToAdd,
      normalizeDraftAccessForConnection,
    ],
  );
  // "Apply to all" must not imply that WRITE is available for an account
  // whose Project assignment is intentionally capped at READ.
  // Batch controls appear only when every account can receive the exact same
  // access level. Otherwise, an "Apply to all" label would hide meaningful
  // per-account differences such as a read-only Project assignment or a
  // connection the selected user owns.
  const canApplySameAccessToAllConnections = useMemo(
    () =>
      editableActiveConnections.length > 0 &&
      editableActiveConnections.every(
        (connection) =>
          !isProjectConnectionOwner(connection, memberToAdd) &&
          getConnectionAccessLevelsForCeiling(connection, memberToAdd).includes(
            "action",
          ),
      ),
    [
      editableActiveConnections,
      getConnectionAccessLevelsForCeiling,
      memberToAdd,
    ],
  );
  const shouldExplainIndividualConnectionAccess =
    editableActiveConnections.length > 1 && !canApplySameAccessToAllConnections;
  const selectedBatchConnection = useMemo(
    () =>
      dialog.kind === "batch-grant-access"
        ? manageableActiveConnections.find(
            (connection) => connection.id === dialog.connectionId,
          ) || null
        : null,
    [dialog, manageableActiveConnections],
  );
  const getBatchAssignableMembers = useCallback(
    (connection: AssignableBrandConnection) =>
      activeBusinessGroupMembers.filter(
        (member) =>
          member.id !== activeProjectCreatorId &&
          !isProjectConnectionOwner(connection, member.id),
      ),
    [activeBusinessGroupMembers, activeProjectCreatorId],
  );
  const batchAssignableMembers = useMemo(
    () =>
      selectedBatchConnection
        ? getBatchAssignableMembers(selectedBatchConnection)
        : [],
    [getBatchAssignableMembers, selectedBatchConnection],
  );
  const resolveBatchMemberAccessConnection = useCallback(
    (target: BusinessGroupMemberAccessTarget) =>
      resolveBusinessGroupMemberAccessConnection(
        activeConnections,
        target,
      ),
    [activeConnections],
  );
  const getBatchMemberAccessActionAvailability = useCallback(
    (target: BusinessGroupMemberAccessTarget) => {
      const connection = resolveBatchMemberAccessConnection(target);
      return resolveBusinessGroupMemberAccessActionAvailability({
        canManage: canManageActiveBrandMemberAccess,
        isReady: projectConnectionGrantsReady,
        hasConnection: Boolean(connection),
        assignableMemberCount: connection
          ? getBatchAssignableMembers(connection).length
          : 0,
      });
    },
    [
      canManageActiveBrandMemberAccess,
      getBatchAssignableMembers,
      projectConnectionGrantsReady,
      resolveBatchMemberAccessConnection,
    ],
  );
  const canOpenBatchGrantAccessDialog = useCallback(
    (target: BusinessGroupMemberAccessTarget) =>
      getBatchMemberAccessActionAvailability(target).enabled,
    [getBatchMemberAccessActionAvailability],
  );
  const getBatchGrantAccessDisabledReason = useCallback(
    (target: BusinessGroupMemberAccessTarget) => {
      const { disabledReason } = getBatchMemberAccessActionAvailability(target);
      if (disabledReason === "permission-denied") {
        return t(
          "website.brandsSettings.simple.manageMembersPermissionDenied",
        );
      }
      if (disabledReason === "loading") {
        return t(
          "website.integrationsSettings.loadingBusinessGroupMemberAccountAccess",
        );
      }
      if (disabledReason === "connection-unavailable") {
        return t("website.integrationsSettings.connectionAccessUnavailable");
      }
      if (disabledReason === "no-assignable-members") {
        return t(
          "website.brandsSettings.simple.addMembersBeforeManagingAccess",
        );
      }
      return undefined;
    },
    [getBatchMemberAccessActionAvailability, t],
  );
  const getMemberApplicationAccessActionAvailability = useCallback(
    (targetUserId: string) =>
      resolveBusinessGroupApplicationAccessActionAvailability({
        canManage: canManageActiveBrandMemberAccess,
        isReady: projectConnectionGrantsReady,
        applicationCount: activeConnections.length,
        manageableApplicationCount:
          getManageableActiveConnectionsForUser(targetUserId).length,
      }),
    [
      activeConnections.length,
      canManageActiveBrandMemberAccess,
      getManageableActiveConnectionsForUser,
      projectConnectionGrantsReady,
    ],
  );
  const getMemberApplicationAccessDisabledReason = useCallback(
    (targetUserId: string) => {
      const { disabledReason } =
        getMemberApplicationAccessActionAvailability(targetUserId);
      if (disabledReason === "permission-denied") {
        return t(
          "website.brandsSettings.simple.manageApplicationAccessPermissionDenied",
        );
      }
      if (disabledReason === "loading") {
        return t(
          "website.integrationsSettings.loadingBusinessGroupMemberAccountAccess",
        );
      }
      if (disabledReason === "no-applications") {
        return t(
          "website.brandsSettings.simple.assignApplicationBeforeManagingAccess",
        );
      }
      if (disabledReason === "connection-unavailable") {
        return t("website.integrationsSettings.connectionAccessUnavailable");
      }
      return undefined;
    },
    [
      getMemberApplicationAccessActionAvailability,
      t,
    ],
  );
  const canApplySameBatchAccessToAllMembers = useMemo(() => {
    if (!selectedBatchConnection) {
      return false;
    }

    return (
      batchAssignableMembers.length > 0 &&
      batchAssignableMembers.every((member) =>
        getConnectionAccessLevelsForCeiling(
          selectedBatchConnection,
          member.id,
        ).includes("action"),
      )
    );
  }, [
    batchAssignableMembers,
    getConnectionAccessLevelsForCeiling,
    selectedBatchConnection,
  ]);
  const shouldExplainIndividualBatchAccess =
    batchAssignableMembers.length > 1 && !canApplySameBatchAccessToAllMembers;
  const canEditActiveBrand = Boolean(
    !brandGroupAccess.isReadOnly && isActiveProjectManager,
  );
  const canManageAnyActiveConnection = manageableActiveConnections.length > 0;
  const canAssignApplicationAccess = Boolean(
    canManageAnyActiveConnection &&
    !brandGroupAccess.isReadOnly &&
    isActiveProjectManager &&
    activeBrand?.scope === "workspace",
  );
  const canAddBusinessGroupMembers = canManageBusinessGroupMembers({
    isDefault: isActiveBrandDefault,
    scope: activeBrand?.scope,
    isProjectOwner: isActiveProjectOwner,
    isWorkspaceOwner,
    isReadOnly: brandGroupAccess.isReadOnly,
  });
  const canSaveNewBusinessGroupMember = Boolean(
    memberToAdd &&
    canAddBusinessGroupMembers &&
    editableActiveConnections.length > 0,
  );
  const hasDuplicateBrandName = useMemo(
    () =>
      hasDuplicateBusinessGroupName({
        existingBusinessGroups: data.brands,
        businessGroupName: brandName,
      }),
    [brandName, data.brands],
  );
  // Do not compare a successful in-flight submission with the newly-created
  // item after the shared list refreshes.
  const hasBrandNameConflict =
    hasDuplicateBrandName && dialogSubmissionRef.current !== "creating-brand";
  const canDeleteActiveBrand = Boolean(
    !brandGroupAccess.isReadOnly &&
    activeBrand &&
    !isActiveBrandDefault &&
    isActiveProjectManager,
  );
  const hasProjectIdentityChanges = Boolean(
    targetBrandForEdit &&
    settingsDraft?.brandId === targetBrandForEdit.id &&
    (settingsDraft.name.trim() !== targetBrandForEdit.name ||
      settingsDraft.logoUrl !== (targetBrandForEdit.logoUrl || null)),
  );
  const hasSettingsChanges = Boolean(
    activeBrand &&
    settingsDraft?.brandId === activeBrand.id &&
    (settingsDraft.name.trim() !== activeBrand.name ||
      settingsDraft.domain.trim() !== activeBrand.domain ||
      settingsDraft.market.trim() !== (activeBrand.market || "") ||
      settingsDraft.description.trim() !== activeBrand.description ||
      settingsDraft.logoUrl !== (activeBrand.logoUrl || null)),
  );
  const brandConnectionsById = useMemo(
    () =>
      new Map(
        data.brands.map((brand) => [
          brand.id,
          brandConnections.filter((connection) =>
            connection.brandIds.includes(brand.id),
          ),
        ]),
      ),
    [brandConnections, data.brands],
  );
  const brandUsersById = useMemo(
    () =>
      new Map(
        data.brands.map((brand) => {
          if (brand.isDefault) {
            return [brand.id, workspaceUsers];
          }

          const userIdsInBrand = new Set(
            data.memberships
              .filter((membership) => membership.brandId === brand.id)
              .map((membership) => membership.userId),
          );
          return [
            brand.id,
            workspaceUsers.filter((workspaceUser) =>
              userIdsInBrand.has(workspaceUser.id),
            ),
          ];
        }),
      ),
    [data.brands, data.memberships, workspaceUsers],
  );

  const openBrand = (
    brand: BrandRecord,
    tab: BrandsSettingsDetailTab = "integrations",
  ) => {
    setSettingsDraft(createBrandSettingsDraft(brand));
    changePage({ kind: "detail", brandId: brand.id, tab });
  };

  const openEditBrandDialog = (brand: BrandRecord) => {
    if (brandGroupAccess.isReadOnly || !isProjectManager(brand)) {
      return;
    }

    setSettingsDraft(createBrandSettingsDraft(brand));
    setDialog({ kind: "edit-brand", brandId: brand.id });
  };

  const setActiveTab = (tab: BrandsSettingsDetailTab) => {
    if (page.kind === "detail") {
      changePage({ ...page, tab });
    }
  };

  const resetCreateBrandDraft = () => {
    setBrandName("");
    setBrandDomain("");
    setBrandMarket("");
    setBrandDescription("");
    setBrandLogoUrl(null);
    setMemberToAdd("");
    setConnectionAccessDraft({});
    setBrandScope(canCreateWorkspaceProject ? "workspace" : "private");
  };

  const openCreateBrandDialog = () => {
    if (dialogSubmissionRef.current !== "idle") {
      return;
    }
    resetCreateBrandDraft();
    setDialog({ kind: "create-brand" });
  };

  const openBrandLimitDialog = () => {
    setDialog({ kind: "brand-limit" });
  };

  const openBrandGroupPricing = () => {
    modal.openModal("pricing", {
      data: {
        workspaceId: currentWorkspace?.id,
        preferredTeamOfferCode: brandGroupAccess.preferredTeamOfferCode,
      },
    });
  };

  const handleCreateBrandEntry = () => {
    if (
      !canUseMultipleBusinessGroupsInWorkspace ||
      !canCreateProject ||
      dialogSubmissionRef.current !== "idle"
    ) {
      return;
    }
    if (brandGroupAccess.kind === "limit_reached") {
      openBrandLimitDialog();
      return;
    }
    if (!brandGroupAccess.canCreate) {
      openBrandGroupPricing();
      return;
    }
    openCreateBrandDialog();
  };

  const closeCreateBrandDialog = () => {
    if (dialogSubmissionRef.current !== "idle") {
      return;
    }
    resetCreateBrandDraft();
    setDialog({ kind: "closed" });
  };

  const handleCreateBrand = async () => {
    if (isSavingProjectAction || dialogSubmissionRef.current !== "idle") {
      return;
    }
    if (!canUseMultipleBusinessGroupsInWorkspace) {
      closeCreateBrandDialog();
      return;
    }
    if (!canCreateProject) {
      if (projectCreatePermissionDenied) {
        showToast(projectCreatePermissionMessage, "error");
      }
      return;
    }
    if (!brandGroupAccess.canCreate) {
      resetCreateBrandDraft();
      if (brandGroupAccess.kind === "limit_reached") {
        openBrandLimitDialog();
      } else {
        setDialog({ kind: "closed" });
        openBrandGroupPricing();
      }
      return;
    }
    const name = brandName.trim();
    if (!name || hasBrandNameConflict || !currentPrototypeUserId) {
      return;
    }
    if (!beginDialogSubmission("creating-brand")) {
      return;
    }
    try {
      const brandId = await createProject({
        scope: brandScope,
        name,
        domain: brandDomain.trim(),
        market: brandMarket.trim(),
        description:
          brandDescription.trim() ||
          t("website.brandsSettings.simple.newBrandDescription"),
        logoUrl: brandLogoUrl,
      });

      const creationFollowUp = getBusinessGroupCreationFollowUp(brandId);

      resetCreateBrandDraft();
      setSettingsDraft(null);
      changePage(creationFollowUp.page);
      setDialog(creationFollowUp.dialog);
      showActionToast({
        title: t("website.brandsSettings.simple.brandCreatedTitle"),
        description: t(
          "website.brandsSettings.simple.brandCreatedWithoutApps",
          {
            brand: name,
          },
        ),
        type: "success",
        duration: BRAND_CREATED_TOAST_DURATION_MS,
      });
    } catch (error: unknown) {
      if (getProjectCreateHttpStatus(error) === 403) {
        showToast(projectCreatePermissionMessage, "error");
      } else {
        showToast(
          t("website.brandsSettings.simple.projectCreateFailed"),
          "error",
        );
      }
    } finally {
      finishDialogSubmission("creating-brand");
    }
  };

  const handleDeleteBrand = async () => {
    if (dialog.kind !== "delete-brand") {
      return;
    }
    const brandToDelete = targetBrandForDelete;
    if (
      !brandToDelete ||
      brandToDelete.isDefault ||
      !isProjectManager(brandToDelete)
    ) {
      return;
    }

    if (isSavingProjectAction) return;
    setIsSavingProjectAction(true);
    try {
      await deleteProject(brandToDelete.id);
      setDialog({ kind: "closed" });
      if (page.kind === "detail" && page.brandId === brandToDelete.id) {
        changePage({ kind: "directory" });
      }
    } catch {
      showToast(
        t("website.integrationsSettings.failedToSaveConnectionAccess"),
        "error",
      );
    } finally {
      setIsSavingProjectAction(false);
    }
  };

  const updateConnectionBrandAssignment = async (
    connectionId: string,
    brandIds: string[],
  ): Promise<void> => {
    const connection = brandConnections.find(
      (item) => item.id === connectionId,
    );
    const resource = connection?.resource;
    const integrationConnectionId = Number(
      connection?.integrationConnectionId || connection?.id,
    );
    if (!connection || !Number.isFinite(integrationConnectionId)) {
      return;
    }

    const currentBrandIds = new Set(connection.brandIds);
    const nextBrandIds = new Set(brandIds);
    const addedBrandIds = brandIds.filter(
      (brandId) => !currentBrandIds.has(brandId),
    );
    const removedBrandIds = connection.brandIds.filter(
      (brandId) => !nextBrandIds.has(brandId),
    );

    for (const brandId of addedBrandIds) {
      await assignProjectIntegration(brandId, integrationConnectionId);
      if (resource) await assignProjectResource(brandId, resource);
    }
    for (const brandId of removedBrandIds) {
      if (resource) await removeProjectResource(brandId, resource);
      const hasAnotherAssignedResource = brandConnections.some(
        (candidate) =>
          candidate.id !== connection.id &&
          candidate.integrationConnectionId ===
            connection.integrationConnectionId &&
          candidate.brandIds.includes(brandId),
      );
      if (!hasAnotherAssignedResource) {
        await removeProjectIntegration(brandId, integrationConnectionId);
      }
    }
  };

  const handleRemoveConnection = async () => {
    if (
      !activeBrand ||
      dialog.kind !== "remove-connection" ||
      isSavingProjectAction
    ) {
      return;
    }

    const connection = brandConnections.find(
      (item) => item.id === dialog.connectionId,
    );
    if (!connection) {
      return;
    }

    setIsSavingProjectAction(true);
    try {
      await updateConnectionBrandAssignment(
        connection.id,
        connection.brandIds.filter((brandId) => brandId !== activeBrand.id),
      );
      setDialog({ kind: "closed" });
    } catch {
      showToast(
        t("website.integrationsSettings.failedToSaveConnectionAccess"),
        "error",
      );
    } finally {
      setIsSavingProjectAction(false);
    }
  };

  const loadConnectionAccessDraft = (userId: string) => {
    if (!activeBrand) {
      return;
    }

    setConnectionAccessDraft(
      Object.fromEntries(
        getManageableActiveConnectionsForUser(userId).map((connection) => [
          connection.id,
          normalizeDraftAccessForConnection(
            connection,
            resolveBrandUserConnectionAccess(connection, userId),
            userId,
          ),
        ]),
      ),
    );
  };

  const setAllConnectionAccess = (accessLevel: BrandConnectionAccessLevel) => {
    setConnectionAccessDraft(
      Object.fromEntries(
        editableActiveConnections.map((connection) => [
          connection.id,
          normalizeDraftAccessForConnection(
            connection,
            accessLevel,
            memberToAdd,
          ),
        ]),
      ),
    );
  };

  const loadBatchMemberAccessDraft = (
    connection: AssignableBrandConnection,
  ) => {
    setBatchMemberAccessDraft(
      Object.fromEntries(
        getBatchAssignableMembers(connection).map((member) => [
          member.id,
          normalizeDraftAccessForConnection(
            connection,
            resolveBrandUserConnectionAccess(connection, member.id),
            member.id,
          ),
        ]),
      ),
    );
  };

  const setAllBatchMemberAccess = (accessLevel: BrandConnectionAccessLevel) => {
    if (!selectedBatchConnection) {
      return;
    }

    setBatchMemberAccessDraft(
      Object.fromEntries(
        batchAssignableMembers.map((member) => [
          member.id,
          normalizeDraftAccessForConnection(
            selectedBatchConnection,
            accessLevel,
            member.id,
          ),
        ]),
      ),
    );
  };

  const openGrantAccessDialog = (userId?: string) => {
    const canOpenDialog = userId
      ? getMemberApplicationAccessActionAvailability(userId).enabled
      : isActiveBrandDefault
        ? canManageAnyActiveConnection
        : canAddBusinessGroupMembers;
    if (
      !activeBrand ||
      !hasLoadedProjectConnectionGrants ||
      !canOpenDialog ||
      dialogSubmissionRef.current !== "idle"
    ) {
      return;
    }

    setMemberToAdd(userId || "");
    if (userId) {
      loadConnectionAccessDraft(userId);
    } else {
      setConnectionAccessDraft(
        Object.fromEntries(
          manageableActiveConnections.map((connection) => [
            connection.id,
            "no_access",
          ]),
        ),
      );
    }
    setDialog({ kind: "grant-access", userId, mode: "workspace-member" });
  };

  const openInviteBusinessGroupMember = () => {
    if (
      !activeBrand ||
      !canInviteNewWorkspaceMemberToActiveBusinessGroup ||
      dialogSubmissionRef.current !== "idle"
    ) {
      return;
    }
    if (workspaceMemberInviteEligibility === "upgrade-required") {
      setDialog({ kind: "closed" });
      setIsInviteUpgradeModalOpen(true);
      return;
    }
    if (workspaceMemberInviteEligibility === "member-limit-reached") {
      setDialog({ kind: "closed" });
      setIsInviteLimitModalOpen(true);
      return;
    }
    if (workspaceMemberInviteEligibility !== "allowed") {
      return;
    }
    setMemberToAdd("");
    setInviteEmail("");
    setInviteEmailError("");
    setConnectionAccessDraft(
      Object.fromEntries(
        manageableActiveConnections.map((connection) => [
          connection.id,
          "no_access",
        ]),
      ),
    );
    setDialog({ kind: "grant-access", mode: "invite-new-member" });
  };

  const handleOpenInviteUpgradePricing = () => {
    setIsInviteUpgradeModalOpen(false);
    modal.openModal("pricing");
  };

  const handleInviteLimitPrimaryAction = () => {
    setIsInviteLimitModalOpen(false);
    if (workspaceInviteLimitPrompt.primaryActionTarget === "sales") {
      setIsInviteLimitContactSalesOpen(true);
      return;
    }
    if (workspaceInviteLimitPrompt.primaryActionTarget === "plans") {
      modal.openModal("pricing");
    }
  };

  const returnToWorkspaceMemberSelection = () => {
    if (dialogSubmissionRef.current !== "idle") {
      return;
    }
    setInviteEmail("");
    setInviteEmailError("");
    setMemberToAdd("");
    setConnectionAccessDraft(
      Object.fromEntries(
        manageableActiveConnections.map((connection) => [
          connection.id,
          "no_access",
        ]),
      ),
    );
    setDialog({ kind: "grant-access", mode: "workspace-member" });
  };

  const sendBusinessGroupMemberInvite = async () => {
    if (
      !activeBrand ||
      !currentWorkspace?.id ||
      !isBusinessGroupInviteMode ||
      !canInviteNewWorkspaceMemberToActiveBusinessGroup ||
      workspaceMemberInviteEligibility !== "allowed" ||
      inviteRoleLoadState !== "ready" ||
      !inviteRoleId ||
      dialogSubmissionRef.current !== "idle"
    ) {
      return;
    }

    const normalizedEmail = inviteEmail.trim().toLowerCase();
    if (!normalizedEmail) {
      setInviteEmailError(
        t("website.brandsSettings.simple.inviteEmailRequired"),
      );
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setInviteEmailError(
        t("website.brandsSettings.simple.inviteEmailInvalid"),
      );
      return;
    }
    if (
      workspaceUsers.some(
        (workspaceUser) =>
          workspaceUser.email.trim().toLowerCase() === normalizedEmail,
      )
    ) {
      setInviteEmailError(
        t("website.brandsSettings.simple.inviteExistingWorkspaceUser"),
      );
      return;
    }

    const dataRoleId = Number(inviteRoleId);
    if (!Number.isFinite(dataRoleId)) {
      showToast(
        t("website.brandsSettings.simple.inviteRoleUnavailable"),
        "error",
      );
      return;
    }
    if (!beginDialogSubmission("inviting-business-group-member")) {
      return;
    }

    try {
      const inviteDraft = Object.fromEntries(
        editableActiveConnections.map((connection) => [
          getInviteProjectAccessDraftKey(
            activeBrand.id,
            connection.assignmentKey,
          ),
          connectionAccessDraft[connection.id] || "no_access",
        ]),
      );
      const projectAccessGrants = buildInviteProjectAccessGrants(
        editableActiveConnections,
        [activeBrand.id],
        inviteDraft,
      );
      await workspaceMembers.inviteMember(
        normalizedEmail,
        "MEMBER",
        dataRoleId,
        projectAccessGrants,
      );
      setDialog({ kind: "closed" });
      setInviteEmail("");
      setInviteEmailError("");
      setConnectionAccessDraft({});
      showToast(
        t("website.brandsSettings.simple.businessGroupInvitationSent", {
          email: normalizedEmail,
        }),
        "success",
      );
    } catch {
      showToast(
        t("website.brandsSettings.simple.businessGroupInvitationFailed"),
        "error",
      );
    } finally {
      finishDialogSubmission("inviting-business-group-member");
    }
  };

  const openBatchGrantAccessDialog = (
    target: BusinessGroupMemberAccessTarget,
  ) => {
    const selectedConnection = resolveBatchMemberAccessConnection(target);
    if (
      !selectedConnection ||
      !getBatchMemberAccessActionAvailability(target).enabled
    ) {
      return;
    }
    loadBatchMemberAccessDraft(selectedConnection);
    setDialog({
      kind: "batch-grant-access",
      connectionId: selectedConnection.id,
    });
  };

  const saveBatchConnectionAccess = async () => {
    if (
      !activeBrand ||
      !selectedBatchConnection ||
      dialog.kind !== "batch-grant-access" ||
      isSavingProjectAction
    ) {
      return;
    }

    setIsSavingProjectAction(true);
    try {
      if (!currentWorkspace?.id) {
        throw new Error("workspace_required");
      }
      const connectionId = Number(
        selectedBatchConnection.integrationConnectionId ||
          selectedBatchConnection.id,
      );
      if (!Number.isFinite(connectionId)) {
        throw new Error("project_connection_identity_required");
      }
      const resource = selectedBatchConnection.resource;
      const secondaryResource = isSecondaryProjectResource(resource);
      const grantUpdates = batchAssignableMembers.map((member) => {
        const accessLevel = normalizeDraftAccessForConnection(
          selectedBatchConnection,
          batchMemberAccessDraft[member.id] || "no_access",
          member.id,
        );
        return {
          connection: selectedBatchConnection,
          userId: member.id,
          accessLevel,
          grant: {
            userId: member.id,
            connectionId,
            resourceType: secondaryResource
              ? resource!.resourceType
              : "CONNECTION",
            resourceKey: secondaryResource
              ? resource!.resourceKey
              : String(connectionId),
            accessLevel:
              accessLevel === "action"
                ? "WRITE"
                : accessLevel === "read"
                  ? "READ"
                  : "DISABLED",
          } as const,
        };
      });

      await projectService.updateProjectMemberAccessGrants(
        currentWorkspace.id,
        activeBrand.id,
        grantUpdates.map(({ grant }) => grant),
      );

      setProjectConnectionGrants((current) => ({
        ...current,
        ...Object.fromEntries(
          grantUpdates.map(({ connection, userId, accessLevel }) => [
            projectGrantIdentity(connection, userId),
            accessLevel,
          ]),
        ),
      }));
      setHasLoadedProjectConnectionGrants(true);
      await refreshBrandGroup();
      setDialog({ kind: "closed" });
      showToast(
        t("website.brandsSettings.simple.batchAccessUpdated", {
          count: batchAssignableMembers.length,
        }),
        "success",
      );
    } catch (error) {
      showToast(
        error instanceof Error &&
          error.message.includes("project_connection_owner_write_required")
          ? t("website.brandsSettings.simple.connectionOwnerActionRequired")
          : t("website.integrationsSettings.failedToSaveConnectionAccess"),
        "error",
      );
    } finally {
      setIsSavingProjectAction(false);
    }
  };

  const saveConnectionAccess = async () => {
    if (
      !activeBrand ||
      !memberToAdd ||
      dialog.kind !== "grant-access" ||
      (dialog.userId
        ? editableActiveConnections.length === 0
        : isActiveBrandDefault
          ? !canAssignApplicationAccess || !hasPositiveConnectionAccessDraft
          : !canSaveNewBusinessGroupMember) ||
      isSavingProjectAction ||
      dialogSubmissionRef.current !== "idle"
    ) {
      return;
    }

    if (!beginDialogSubmission("adding-business-group-member")) {
      return;
    }
    try {
      const isUpdatingExistingUser = Boolean(dialog.userId);
      if (!currentWorkspace?.id) {
        throw new Error("workspace_required");
      }
      const grantUpdates = editableActiveConnections.map((connection) => {
        const accessLevel = normalizeDraftAccessForConnection(
          connection,
          connectionAccessDraft[connection.id] || "no_access",
          memberToAdd,
        );
        const connectionId = Number(
          connection.integrationConnectionId || connection.id,
        );
        if (!Number.isFinite(connectionId)) {
          throw new Error("project_connection_identity_required");
        }
        const resource = connection.resource;
        const secondaryResource = isSecondaryProjectResource(resource);
        return {
          connection,
          accessLevel,
          grant: {
            userId: memberToAdd,
            connectionId,
            resourceType: secondaryResource
              ? resource!.resourceType
              : "CONNECTION",
            resourceKey: secondaryResource
              ? resource!.resourceKey
              : String(connectionId),
            accessLevel:
              accessLevel === "action"
                ? "WRITE"
                : accessLevel === "read"
                  ? "READ"
                  : "DISABLED",
          } as const,
        };
      });
      // Selecting a new Workspace user here is an explicit Business-group
      // membership action. Editing an existing row only changes grants.
      // Application assignment flows must never call this membership API.
      if (!isUpdatingExistingUser && !isActiveBrandDefault) {
        await projectService.updateProjectMemberResourceGrants(
          currentWorkspace.id,
          activeBrand.id,
          { userId: memberToAdd, role: "VIEW" },
          grantUpdates.map(({ grant }) => grant),
        );
      } else {
        await projectService.updateProjectResourceGrants(
          currentWorkspace.id,
          activeBrand.id,
          grantUpdates.map(({ grant }) => grant),
        );
      }
      setProjectConnectionGrants((current) => ({
        ...current,
        ...Object.fromEntries(
          grantUpdates.map(({ connection, accessLevel }) => [
            projectGrantIdentity(connection, memberToAdd),
            accessLevel,
          ]),
        ),
      }));
      setHasLoadedProjectConnectionGrants(true);
      await refreshBrandGroup();
      setMemberToAdd("");
      setConnectionAccessDraft({});
      setDialog({ kind: "closed" });
      showToast(
        t(
          isUpdatingExistingUser || isActiveBrandDefault
            ? "website.brandsSettings.simple.appAccessUpdated"
            : "website.brandsSettings.simple.userAccessAdded",
        ),
        "success",
      );
    } catch (error) {
      showToast(
        error instanceof Error &&
          error.message.includes("project_connection_owner_write_required")
          ? t("website.brandsSettings.simple.connectionOwnerActionRequired")
          : t("website.integrationsSettings.failedToSaveConnectionAccess"),
        "error",
      );
    } finally {
      finishDialogSubmission("adding-business-group-member");
    }
  };

  const handleSaveSettings = async () => {
    if (!activeBrand || !settingsDraft?.name.trim() || isSavingProjectAction) {
      return;
    }
    const nextSettings = {
      name: settingsDraft.name.trim(),
      domain: settingsDraft.domain.trim(),
      market: settingsDraft.market.trim(),
      description: settingsDraft.description.trim(),
      logoUrl: settingsDraft.logoUrl,
    };

    setIsSavingProjectAction(true);
    try {
      await updateProject(activeBrand.id, nextSettings);
      setSettingsDraft({ brandId: activeBrand.id, ...nextSettings });
      showToast(
        t("website.brandsSettings.simple.brandSettingsSaved"),
        "success",
      );
    } catch {
      showToast(
        t("website.integrationsSettings.failedToSaveConnectionAccess"),
        "error",
      );
    } finally {
      setIsSavingProjectAction(false);
    }
  };

  const handleSaveProjectIdentity = async () => {
    if (
      !targetBrandForEdit ||
      !settingsDraft?.name.trim() ||
      brandGroupAccess.isReadOnly ||
      !isProjectManager(targetBrandForEdit)
    ) {
      return;
    }
    if (isSavingProjectAction) return;

    const nextSettings = {
      name: settingsDraft.name.trim(),
      domain: targetBrandForEdit.domain,
      market: targetBrandForEdit.market,
      description: targetBrandForEdit.description,
      logoUrl: settingsDraft.logoUrl,
    };

    setIsSavingProjectAction(true);
    try {
      await updateProject(targetBrandForEdit.id, nextSettings);
      setSettingsDraft({ brandId: targetBrandForEdit.id, ...nextSettings });
      setDialog({ kind: "closed" });
      showToast(
        t("website.brandsSettings.simple.brandSettingsSaved"),
        "success",
      );
    } catch {
      showToast(
        t("website.integrationsSettings.failedToSaveConnectionAccess"),
        "error",
      );
    } finally {
      setIsSavingProjectAction(false);
    }
  };

  const readLogoFile = (
    event: ChangeEvent<HTMLInputElement>,
    onLoad: (logoUrl: string) => void,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (
      !file ||
      !file.type.startsWith("image/") ||
      file.size > 2 * 1024 * 1024
    ) {
      return;
    }

    setIsLogoReading(true);
    const reader = new FileReader();
    reader.onload = () => {
      onLoad(String(reader.result));
      setIsLogoReading(false);
    };
    reader.onerror = () => setIsLogoReading(false);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    readLogoFile(event, (logoUrl) =>
      setSettingsDraft((draft) => (draft ? { ...draft, logoUrl } : draft)),
    );
  };

  const handleCreateLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    readLogoFile(event, setBrandLogoUrl);
  };

  if (shouldShowBrandGroupLoadingSkeleton) {
    return (
      <BrandGroupLoadingSkeleton
        detail={page.kind === "detail"}
        label={t("common.loading")}
      />
    );
  }

  if (page.kind === "detail" && !activeBrand) {
    return (
      <Surface className="p-8 text-center">
        <p className="text-sm text-[#666F8D]">
          {t("website.brandsSettings.brandNotFound")}
        </p>
        <div className="mt-4">
          {!isSingleBusinessGroupMode ? (
            <SecondaryButton onClick={() => changePage({ kind: "directory" })}>
              {t("website.brandsSettings.backToBrands")}
            </SecondaryButton>
          ) : null}
        </div>
      </Surface>
    );
  }

  if (brandGroupError && !brandGroupLoading) {
    return (
      <Surface className="flex min-h-[440px] items-center justify-center px-6 py-10 text-center">
        <div className="max-w-[560px]">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF4E5] text-[#B54708]">
            <WarningCircle size={22} weight="regular" />
          </span>
          <p className="mt-4 text-sm leading-6 text-[#B54708]">
            {t("website.brandsSettings.simple.loadFailed")}
          </p>
          <div className="mt-4">
            <SecondaryButton
              onClick={() => void refreshBrandGroup().catch(() => undefined)}
            >
              {t("common.retry")}
            </SecondaryButton>
          </div>
        </div>
      </Surface>
    );
  }

  const billingPermissionHint = canManageBilling
    ? undefined
    : t("website.brandsSettings.simple.billingPermissionHint");
  const hasExistingBrandGroups = brandGroupAccess.currentCount > 0;
  const accessPanelCopy =
    brandGroupAccess.kind === "subscription_expired" && hasExistingBrandGroups
      ? {
          title: t("website.brandsSettings.simple.renewBrandGroupsTitle"),
          description: t(
            "website.brandsSettings.simple.renewBrandGroupsDescription",
            {
              count: brandGroupAccess.currentCount,
            },
          ),
          actionLabel: t("website.brandsSettings.simple.renewSubscription"),
        }
      : brandGroupAccess.kind === "downgraded_read_only" &&
          hasExistingBrandGroups
        ? {
            title: t("website.brandsSettings.simple.upgradeBrandGroupsTitle"),
            description: t(
              "website.brandsSettings.simple.upgradeBrandGroupsDescription",
              {
                count: brandGroupAccess.currentCount,
              },
            ),
            actionLabel: t("website.brandsSettings.simple.upgradePlan"),
          }
        : {
            title: t("website.brandsSettings.simple.organizeAccessTitle"),
            description: t(
              "website.brandsSettings.simple.organizeAccessDescription",
            ),
            actionLabel: t("website.brandsSettings.simple.upgradePlan"),
          };

  if (brandGroupAccess.kind === "feature_not_included") {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-[20px] font-medium tracking-[-0.01em] text-[#1A162F]">
            {t("website.brandsSettings.simple.directoryTitle")}
          </h1>
          <p className="mt-1 text-sm leading-5 text-[#666F8D]">
            {t("website.brandsSettings.simple.directoryDescription")}
          </p>
        </div>
        <BrandGroupAccessPanel
          access={brandGroupAccess}
          title={accessPanelCopy.title}
          description={accessPanelCopy.description}
          actionLabel={accessPanelCopy.actionLabel}
          permissionHint={billingPermissionHint}
          statusLabel={t("website.brandsSettings.simple.readOnlyLabel")}
          onAction={openBrandGroupPricing}
          fullHeight
        />
      </div>
    );
  }

  const activeBrandCreatorUser = activeBrand
    ? (activeBrand.createdByUserId
        ? workspaceUsers.find((user) => user.id === activeBrand.createdByUserId)
        : undefined) ||
      (activeBrand.primaryOwnerUserId
        ? workspaceUsers.find(
            (user) => user.id === activeBrand.primaryOwnerUserId,
          )
        : undefined) ||
      workspaceUsers.find(
        (user) => user.role === "Owner" || user.role === "Admin",
      ) ||
      workspaceUsers[0]
    : undefined;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 max-w-full">
          {activeBrand && !isSingleBusinessGroupMode ? (
            <button
              type="button"
              onClick={() => changePage({ kind: "directory" })}
              className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#666F8D] hover:text-[#1A162F]"
            >
              <ArrowLeft size={16} />
              {t("website.brandsSettings.allBrands")}
            </button>
          ) : null}
          <div className="flex items-center gap-3">
            {activeBrand ? <BrandAvatar brand={activeBrand} /> : null}
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1
                  className={
                    activeBrand
                      ? "text-2xl font-medium leading-8 tracking-[-0.02em] text-[#1A162F]"
                      : "text-[20px] font-medium tracking-[-0.01em] text-[#1A162F]"
                  }
                >
                  {activeBrand
                    ? getBusinessGroupDisplayName(activeBrand)
                    : t("website.brandsSettings.simple.directoryTitle")}
                </h1>
                {isActiveBrandDefault ? (
                  <span className="rounded-full bg-[#EEF2FF] px-2 py-0.5 text-xs font-medium text-[#465A8C]">
                    {t("website.brandsSettings.simple.defaultBrand")}
                  </span>
                ) : null}
              </div>
              <div className="mt-1 flex items-center gap-2 text-sm leading-5 text-[#666F8D]">
                <span>
                  {activeBrand
                    ? t("website.brandsSettings.simple.createdBy", {
                        name: activeBrandCreatorUser
                          ? activeBrandCreatorUser.name
                          : "",
                      })
                    : t("website.brandsSettings.simple.directoryDescription")}
                </span>
                {activeBrand && getProjectActions(activeBrand).length > 0 ? (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <button
                        type="button"
                        className="flex h-6 w-6 items-center justify-center rounded-md text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F]"
                        title={t("common.moreOptions") || "更多"}
                      >
                        <DotsThree size={16} weight="bold" />
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        align="start"
                        sideOffset={4}
                        className="z-[9999] min-w-[140px] rounded-xl border border-[#E8EBF3] bg-white p-1.5 shadow-lg animate-in fade-in-50 zoom-in-95"
                      >
                        {getProjectActions(activeBrand).includes("edit") ? (
                          <DropdownMenu.Item
                            onClick={() => openEditBrandDialog(activeBrand)}
                            className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-[#1A162F] outline-none hover:bg-[#F4F5F8] focus:bg-[#F4F5F8]"
                          >
                            <PencilSimple size={14} weight="regular" />
                            <span>
                              {t("website.brandsSettings.simple.editBrandName")}
                            </span>
                          </DropdownMenu.Item>
                        ) : null}
                        {getProjectActions(activeBrand).includes("delete") ? (
                          <>
                            <div
                              className="my-1 h-px bg-[#E8EBF3]"
                              role="separator"
                            />
                            <DropdownMenu.Item
                              onClick={() => {
                                setDialog({
                                  kind: "delete-brand",
                                  brandId: activeBrand.id,
                                });
                              }}
                              className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-[#B42318] outline-none hover:bg-[#FEF3F2] focus:bg-[#FEF3F2]"
                            >
                              <Trash size={14} />
                              <span>
                                {t("website.brandsSettings.simple.deleteBrand")}
                              </span>
                            </DropdownMenu.Item>
                          </>
                        ) : null}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        {!activeBrand &&
        !brandGroupAccess.isReadOnly &&
        canUseMultipleBusinessGroupsInWorkspace ? (
          <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-3">
            <div className="flex items-center gap-1">
              <p className="whitespace-nowrap text-xs font-medium text-[#666F8D]">
                {brandGroupAccess.limit === "unlimited"
                  ? t(
                      "website.brandsSettings.simple.brandGroupUsageUnlimited",
                      {
                        current: brandGroupAccess.currentCount,
                      },
                    )
                  : t("website.brandsSettings.simple.brandGroupUsage", {
                      current: brandGroupAccess.currentCount,
                      limit: brandGroupAccess.limit,
                    })}
              </p>
              <Tooltip
                content={
                  <div className="space-y-2">
                    <p className="text-xs font-medium">
                      {t("website.brandsSettings.simple.projectUsageByCreator")}
                    </p>
                    {projectCreatorCounts.length > 0 ? (
                      <div className="space-y-1.5">
                        {projectCreatorCounts.map((creator) => (
                          <div
                            key={creator.id}
                            className="grid grid-cols-[minmax(0,1fr)_88px] items-center gap-4"
                          >
                            <span className="truncate">{creator.name}</span>
                            <span className="grid w-[88px] grid-cols-2 gap-2 text-white/75">
                              <span
                                aria-label={`${t("website.brandsSettings.simple.scopeWorkspace")}: ${creator.workspaceCount}`}
                                className="inline-flex w-10 items-center gap-1 tabular-nums"
                              >
                                <Users
                                  aria-hidden="true"
                                  size={14}
                                  weight="regular"
                                />
                                <span>{creator.workspaceCount}</span>
                              </span>
                              <span
                                aria-label={`${t("website.brandsSettings.simple.scopePrivate")}: ${creator.privateCount}`}
                                className="inline-flex w-10 items-center gap-1 tabular-nums"
                              >
                                <LockKey
                                  aria-hidden="true"
                                  size={14}
                                  weight="regular"
                                />
                                <span>{creator.privateCount}</span>
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-white/75">
                        {t("website.brandsSettings.simple.noProjectsYet")}
                      </p>
                    )}
                  </div>
                }
                side="bottom"
                align="end"
                contentClassName="!max-w-[320px] !whitespace-normal !px-3 !py-2 !text-left !leading-5"
              >
                <button
                  type="button"
                  aria-label={t(
                    "website.brandsSettings.simple.projectUsageByCreator",
                  )}
                  className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[#8A93B2] transition-colors hover:text-[#1A162F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
                >
                  <Question size={14} weight="bold" />
                </button>
              </Tooltip>
            </div>
            <ProjectCreatePermissionTooltip
              blocked={projectCreatePermissionDenied}
              content={projectCreatePermissionMessage}
            >
              <PrimaryButton
                onClick={handleCreateBrandEntry}
                disabled={!canCreateProject}
              >
                <Plus size={17} />
                {t("website.brandsSettings.simple.createAction")}
              </PrimaryButton>
            </ProjectCreatePermissionTooltip>
          </div>
        ) : null}
      </div>

      {!activeBrand ? (
        <div className="space-y-4">
          {canUseMultipleBusinessGroupsInWorkspace ? (
            <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setDirectoryScopeFilter("workspace")}
                  className={clsx(
                    "rounded-full px-3.5 py-1.5 text-xs font-medium transition-all",
                    directoryScopeFilter === "workspace"
                      ? "bg-[#1A162F] text-white shadow-sm"
                      : "text-[#666F8D] hover:bg-black/5 hover:text-[#1A162F]",
                  )}
                >
                  {t("website.brandsSettings.simple.directoryTabWorkspace")}
                  <span className="ml-2 inline-flex min-w-4 justify-center tabular-nums text-current/70">
                    {directoryScopeCounts.workspace}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setDirectoryScopeFilter("private")}
                  className={clsx(
                    "rounded-full px-3.5 py-1.5 text-xs font-medium transition-all",
                    directoryScopeFilter === "private"
                      ? "bg-[#1A162F] text-white shadow-sm"
                      : "text-[#666F8D] hover:bg-black/5 hover:text-[#1A162F]",
                  )}
                >
                  {t("website.brandsSettings.simple.directoryTabPrivate")}
                  <span className="ml-2 inline-flex min-w-4 justify-center tabular-nums text-current/70">
                    {directoryScopeCounts.private}
                  </span>
                </button>
              </div>

              <div className="relative flex items-center min-w-[200px] max-w-[240px]">
                <MagnifyingGlass
                  size={15}
                  className="absolute left-3 text-[#8A93B2] pointer-events-none"
                />
                <input
                  type="text"
                  value={projectSearchQuery}
                  onChange={(e) => setProjectSearchQuery(e.target.value)}
                  placeholder={t(
                    "website.brandsSettings.simple.searchProjectsPlaceholder",
                  )}
                  className="h-9 w-full rounded-full border border-[#E8EBF3] bg-white pl-9 pr-7 text-xs text-[#1A162F] transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:outline-none focus:ring-1 focus:ring-[#1A162F]"
                />
                {projectSearchQuery ? (
                  <button
                    type="button"
                    onClick={() => setProjectSearchQuery("")}
                    className="absolute right-2.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#E8EBF3] text-[10px] text-[#666F8D] hover:bg-[#C8D0E2] hover:text-[#1A162F]"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {filteredDirectoryBrands.length === 0 ? (
            projectSearchQuery.trim() ? (
              <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed border-[#E2E8F0] bg-[#FAFAFC]/60 px-6 py-20 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F1F3F9] text-[#717E9D]">
                  <MagnifyingGlass size={20} weight="bold" />
                </span>
                <p className="mt-4 text-sm font-semibold text-[#1A162F]">
                  {t("website.brandsSettings.simple.noMatchingProjectsTitle")}
                </p>
                <p className="mt-2 max-w-sm text-xs leading-relaxed text-[#8A93B2]">
                  {t("website.brandsSettings.simple.noMatchingProjectsDesc")}
                </p>
                <div className="mt-6">
                  <SecondaryButton onClick={() => setProjectSearchQuery("")}>
                    {t("website.brandsSettings.simple.clearSearch")}
                  </SecondaryButton>
                </div>
              </div>
            ) : (
              <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed border-[#E2E8F0] bg-[#FAFAFC]/60 px-6 py-20 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F1F3F9] text-[#717E9D]">
                  <Briefcase size={20} weight="bold" />
                </span>
                <p className="mt-4 text-sm font-semibold text-[#1A162F]">
                  {t("website.brandsSettings.simple.noProjectsYet")}
                </p>
                <p className="mt-2 max-w-sm text-xs leading-relaxed text-[#8A93B2]">
                  {t("website.brandsSettings.simple.noProjectsCategoryDesc")}
                </p>
                {!brandGroupAccess.isReadOnly &&
                canUseMultipleBusinessGroupsInWorkspace ? (
                  <ProjectCreatePermissionTooltip
                    blocked={projectCreatePermissionDenied}
                    content={projectCreatePermissionMessage}
                    className="mt-6"
                  >
                    <button
                      type="button"
                      onClick={handleCreateBrandEntry}
                      disabled={!canCreateProject}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-[#1A162F] px-4 py-2 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:bg-[#C8D0E2]"
                    >
                      <Plus size={14} />
                      {t("website.brandsSettings.createBrand")}
                    </button>
                  </ProjectCreatePermissionTooltip>
                ) : null}
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
              {filteredDirectoryBrands.map((brand) => {
                const connectionsForBrand =
                  brandConnectionsById.get(brand.id) || [];
                const membersForBrand = brandUsersById.get(brand.id) || [];
                const connectionCount = connectionsForBrand.length;
                const userCount = membersForBrand.length;
                const connectionCountLabel = t(
                  "website.brandsSettings.simple.connectionCount",
                  { count: connectionCount },
                );
                const memberCountLabel = t(
                  "website.brandsSettings.simple.memberCount",
                  { count: userCount },
                );
                const creatorUser =
                  (brand.createdByUserId
                    ? workspaceUsers.find(
                        (user) => user.id === brand.createdByUserId,
                      )
                    : undefined) ||
                  (brand.primaryOwnerUserId
                    ? workspaceUsers.find(
                        (user) => user.id === brand.primaryOwnerUserId,
                      )
                    : undefined) ||
                  workspaceUsers.find(
                    (user) => user.role === "Owner" || user.role === "Admin",
                  ) ||
                  workspaceUsers[0];

                return (
                  <button
                    key={brand.id}
                    type="button"
                    onClick={() => openBrand(brand)}
                    className="group flex flex-col rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] px-4 pb-3 pt-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#C8D0E2] hover:shadow-[0_8px_20px_rgba(26,22,47,0.06)]"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-1 items-center gap-2.5">
                        <BrandAvatar brand={brand} size="small" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-[#1A162F]">
                              {getBusinessGroupDisplayName(brand)}
                            </h2>
                            {brand.isDefault && brand.scope !== "private" ? (
                              <span
                                aria-label={t(
                                  "website.brandsSettings.simple.defaultBrand",
                                )}
                                className="shrink-0 rounded-full bg-[#EEF2FF] px-1.5 py-0.5 text-[10px] font-medium text-[#465A8C]"
                              >
                                {t(
                                  "website.brandsSettings.simple.defaultBrandBadge",
                                )}
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-1 truncate text-xs text-[#8A93B2]">
                            {t("website.brandsSettings.simple.createdBy", {
                              name: creatorUser ? creatorUser.name : "",
                            })}
                          </p>
                        </div>
                      </div>

                      {getProjectActions(brand).length > 0 ? (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="shrink-0"
                        >
                          <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild>
                              <button
                                type="button"
                                className="flex h-6 w-6 items-center justify-center rounded-md text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F]"
                                title={t("common.moreOptions") || "更多"}
                              >
                                <DotsThree size={16} weight="bold" />
                              </button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                              <DropdownMenu.Content
                                align="end"
                                sideOffset={4}
                                className="z-[9999] min-w-[140px] rounded-xl border border-[#E8EBF3] bg-white p-1.5 shadow-lg animate-in fade-in-50 zoom-in-95"
                              >
                                <DropdownMenu.Item
                                  onClick={() => openEditBrandDialog(brand)}
                                  className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-[#1A162F] outline-none hover:bg-[#F4F5F8] focus:bg-[#F4F5F8]"
                                >
                                  <PencilSimple size={14} weight="regular" />
                                  <span>
                                    {t(
                                      "website.brandsSettings.simple.editBrand",
                                    )}
                                  </span>
                                </DropdownMenu.Item>
                                {getProjectActions(brand).includes("delete") ? (
                                  <DropdownMenu.Item
                                    onClick={() => {
                                      setDialog({
                                        kind: "delete-brand",
                                        brandId: brand.id,
                                      });
                                    }}
                                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-[#B42318] outline-none hover:bg-[#FEF3F2] focus:bg-[#FEF3F2]"
                                  >
                                    <Trash size={14} />
                                    <span>
                                      {t(
                                        "website.brandsSettings.simple.deleteBrand",
                                      )}
                                    </span>
                                  </DropdownMenu.Item>
                                ) : null}
                              </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                          </DropdownMenu.Root>
                        </div>
                      ) : null}
                    </div>

                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="mt-4 grid grid-cols-2 items-center gap-2 border-t border-[#E8EBF3]/80 pt-2.5 text-xs text-[#666F8D]"
                    >
                      <BrandCardConnectionPreview
                        connections={connectionsForBrand}
                        countLabel={connectionCountLabel}
                        title={t(
                          "website.brandsSettings.simple.integrationsInBrand",
                        )}
                      />
                      <BrandCardMemberPreview
                        members={membersForBrand}
                        countLabel={memberCountLabel}
                        title={t(
                          "website.brandsSettings.simple.membersInBrand",
                        )}
                      />
                    </div>
                  </button>
                );
              })}
              {shouldShowDirectoryCreateCard ? (
                <ProjectCreatePermissionTooltip
                  blocked={projectCreatePermissionDenied}
                  content={projectCreatePermissionMessage}
                  className="h-full w-full"
                >
                  <button
                    type="button"
                    onClick={handleCreateBrandEntry}
                    disabled={!canCreateProject}
                    className="flex h-full min-h-[116px] w-full flex-col items-center justify-center rounded-xl border border-dashed border-[#C8D0E2] bg-[#FCFCFC] p-4 text-center transition-colors hover:border-[#8A93B2] hover:bg-[#F8F9FC] disabled:bg-[#F4F5F8] disabled:opacity-60"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F4F5F8] text-[#1A162F]">
                      <Plus size={18} />
                    </span>
                    <span className="mt-2 text-sm font-medium text-[#1A162F]">
                      {t("website.brandsSettings.simple.createAction")}
                    </span>
                  </button>
                </ProjectCreatePermissionTooltip>
              ) : null}
            </div>
          )}
        </div>
      ) : (
        <>
          <nav
            aria-label={t("website.brandsSettings.simple.brandSections")}
            className="flex gap-1 border-b border-[#E8EBF3]"
          >
            {BRAND_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    "relative pb-3 px-1 mr-6 text-sm font-medium transition-colors",
                    isActive
                      ? "text-[#1A162F]"
                      : "text-[#666F8D] hover:text-[#1A162F]",
                  )}
                >
                  {t(`website.brandsSettings.simple.tabs.${tab.id}`)}
                  {isActive && (
                    <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-[#1A162F]" />
                  )}
                </button>
              );
            })}
          </nav>

          {activeTab === "overview" ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Surface className="flex items-center gap-3 px-5 py-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F4F5F8] text-[#666F8D]">
                    <SquaresFour size={18} weight="regular" />
                  </span>
                  <p className="text-2xl font-semibold leading-none text-[#1A162F]">
                    {activeConnections.length}
                  </p>
                  <p className="text-sm text-[#666F8D]">
                    {t("website.brandsSettings.simple.integrationsInBrand")}
                  </p>
                </Surface>
                <Surface className="flex items-center gap-3 px-5 py-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F4F5F8] text-[#666F8D]">
                    <Users size={18} weight="regular" />
                  </span>
                  <p className="text-2xl font-semibold leading-none text-[#1A162F]">
                    {activeBusinessGroupMembers.length}
                  </p>
                  <p className="text-sm text-[#666F8D]">
                    {t("website.brandsSettings.simple.membersInBrand")}
                  </p>
                </Surface>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <Surface className="overflow-hidden">
                  <div className="flex items-start justify-between gap-4 border-b border-[#E8EBF3] px-5 py-4">
                    <div>
                      <h2 className="text-base font-medium text-[#1A162F]">
                        {t("website.brandsSettings.simple.tabs.integrations")}
                      </h2>
                      <p className="mt-1 text-xs leading-5 text-[#666F8D]">
                        {t(
                          "website.brandsSettings.simple.integrationsCardDescription",
                        )}
                      </p>
                    </div>
                    <SecondaryButton
                      onClick={() => setActiveTab("integrations")}
                    >
                      {t("website.brandsSettings.simple.manage")}
                    </SecondaryButton>
                  </div>
                  <div className="divide-y divide-[#E8EBF3]">
                    {activeConnections.slice(0, 3).map((connection) => {
                      const application = getIntegrationBrandIcon(
                        connection.provider,
                      );
                      const connectorName = getBrandConnectionOwnerDisplayName(
                        connection,
                        workspaceUsers,
                      );
                      const connectionMetaLine = formatConnectionMetaLine(
                        t,
                        locale,
                        {
                          connectedAt: connection.connectedAt,
                          connectedBy:
                            connectorName ||
                            t("website.brandsSettings.simple.connectorUnknown"),
                        },
                      );
                      return (
                        <div
                          key={connection.id}
                          className="flex items-center gap-3 px-5 py-3.5"
                        >
                          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-[#E8EBF3] bg-white">
                            <ApplicationLogo application={application} />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-[#1A162F]">
                              {application.name}
                            </p>
                            <p className="mt-0.5 truncate text-xs text-[#3D394D]">
                              {getConnectionDisplayName(connection)}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-[#8A93B2]">
                              {connectionMetaLine}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    {activeConnections.length === 0 ? (
                      <p className="px-5 py-8 text-center text-sm text-[#666F8D]">
                        {t("website.brandsSettings.simple.noIntegrations")}
                      </p>
                    ) : null}
                  </div>
                </Surface>

                <Surface className="overflow-hidden">
                  <div className="flex items-start justify-between gap-4 border-b border-[#E8EBF3] px-5 py-4">
                    <div>
                      <h2 className="text-base font-medium text-[#1A162F]">
                        {t("website.brandsSettings.simple.tabs.members")}
                      </h2>
                      <p className="mt-1 text-xs leading-5 text-[#666F8D]">
                        {t(
                          isActiveBrandDefault
                            ? "website.brandsSettings.simple.defaultBrandMembersCardDescription"
                            : "website.brandsSettings.simple.membersCardDescription",
                        )}
                      </p>
                    </div>
                    <SecondaryButton onClick={() => setActiveTab("members")}>
                      {t("website.brandsSettings.simple.manage")}
                    </SecondaryButton>
                  </div>
                  <div className="divide-y divide-[#E8EBF3]">
                    {activeBusinessGroupMembers.slice(0, 4).map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center gap-3 px-5 py-3.5"
                      >
                        <img
                          src={getMemberAvatarUrl(user)}
                          alt=""
                          className="h-8 w-8 shrink-0 rounded-full border border-gray-200 bg-white object-cover"
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-[#1A162F]">
                            {user.name}
                          </p>
                          <p className="mt-0.5 text-xs text-[#8A93B2]">
                            {t(
                              "website.brandsSettings.simple.applicationAccountAccessCount",
                              {
                                count: user.connectionAccess.length,
                              },
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Surface>
              </div>
            </div>
          ) : null}

          {activeTab === "integrations" ? (
            <Surface className="min-h-[440px] overflow-hidden">
              <div className="border-b border-[#E8EBF3] px-5 py-4">
                <p className="max-w-[620px] text-xs leading-5 text-[#666F8D]">
                  {t(
                    "website.brandsSettings.simple.integrationsClientTaskDescription",
                  )}
                </p>
              </div>
              <div className="p-5">
                {projectIntegrationsBootstrapReady ? (
                  projectConnectionGrantsLoadError ? (
                    <div className="flex items-center justify-center px-5 py-10 text-sm text-[#666F8D]">
                      {t(
                        "website.integrationsSettings.failedToLoadConnectionAccess",
                      )}
                    </div>
                  ) : (
                    <IntegrationsSettings
                      brandId={activeBrand.id}
                      canManageBrand={canManageActiveBrand}
                      canRemoveConnectionsFromBrand={false}
                      onBrowseIntegrations={
                        canAddExistingConnectionsToActiveBrand
                          ? handleGoToIntegrations
                          : undefined
                      }
                      viewMode="connected"
                      hideHeader
                      hideConnectionScopeFilter
                      connectionFilter="all"
                      brandConnectionIds={brandGroupData.connections
                        .filter((connection) =>
                          connection.brandIds.includes(activeBrand.id),
                        )
                        .map((connection) => connection.id)}
                      brandResources={brandGroupData.connections.flatMap(
                        (connection) =>
                          connection.resources.filter((resource) =>
                            resource.brandIds.includes(activeBrand.id),
                          ),
                      )}
                      projectBootstrap={projectIntegrationsBootstrap}
                      workspaceIntegrations={integrations}
                      onSetMemberAccess={
                        openBatchGrantAccessDialog
                      }
                      canSetMemberAccess={canOpenBatchGrantAccessDialog}
                      getSetMemberAccessDisabledReason={
                        getBatchGrantAccessDisabledReason
                      }
                    />
                  )
                ) : (
                  <div className="flex items-center justify-center gap-2 px-5 py-10 text-sm text-[#666F8D]">
                    <CircleNotch size={18} className="animate-spin" />
                    {t(
                      "website.integrationsSettings.loadingBusinessGroupMemberAccountAccess",
                    )}
                  </div>
                )}
              </div>
            </Surface>
          ) : null}

          {activeTab === "members" ? (
            <Surface className="overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#E8EBF3] px-5 py-4">
                <div>
                  <p className="text-xs leading-5 text-[#666F8D]">
                    {t(
                      isActiveBrandDefault
                        ? "website.brandsSettings.simple.defaultBrandMembersDescription"
                        : activeBrand?.scope === "private"
                          ? "website.brandsSettings.simple.privateProjectNotice"
                          : "website.brandsSettings.simple.brandMembersDescription",
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2.5">
                  {!isActiveBrandDefault && canAddBusinessGroupMembers ? (
                    <PrimaryButton
                      disabled={!projectConnectionGrantsReady}
                      onClick={() => openGrantAccessDialog()}
                    >
                      <Plus size={16} />
                      {t("website.brandsSettings.simple.addMemberWithoutAppAccess")}
                    </PrimaryButton>
                  ) : null}
                </div>
              </div>
              <div className="overflow-x-auto">
                {hasLoadedProjectConnectionGrants &&
                activeBusinessGroupMembers.length > 0 ? (
                  <table className="w-full min-w-[520px] border-collapse text-left">
                    <thead className="border-b border-[#E8EBF3] bg-[#FAFBFD] text-xs text-[#8A93B2]">
                      <tr>
                        <th className="px-5 py-3 font-medium">
                          {t("website.brandsSettings.simple.workspaceUser")}
                        </th>
                        <th className="px-5 py-3 font-medium">
                          {t("website.brandsSettings.simple.accessActions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeBusinessGroupMembers.map((brandUser) => {
                        const disabledReason =
                          getMemberApplicationAccessDisabledReason(
                            brandUser.id,
                          );
                        return (
                          <tr
                            key={brandUser.id}
                            className={clsx(
                              "border-b border-[#EEF0F5] last:border-b-0",
                              brandUser.id === activeProjectCreatorId
                                ? "bg-[#F8F9FC] hover:bg-[#F8F9FC]"
                                : "hover:bg-[#FAFBFD]",
                            )}
                          >
                            <td className="px-5 py-4">
                              <div className="flex min-w-0 items-center gap-3">
                                <img
                                  src={getMemberAvatarUrl(brandUser)}
                                  alt=""
                                  className="h-9 w-9 shrink-0 rounded-full border border-gray-200 bg-white object-cover"
                                />
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium text-[#1A162F]">
                                    {brandUser.name}
                                  </p>
                                  <p className="mt-0.5 truncate text-xs text-[#8A93B2]">
                                    {brandUser.email}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex flex-wrap items-center gap-2">
                                <Tooltip content={disabledReason} side="top">
                                  <span
                                    className="inline-flex rounded-[8px] outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
                                    tabIndex={disabledReason ? 0 : undefined}
                                    aria-label={disabledReason}
                                  >
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openGrantAccessDialog(brandUser.id)
                                      }
                                      disabled={Boolean(disabledReason)}
                                      className="whitespace-nowrap rounded-[8px] border border-[#E8EBF3] bg-white px-3 py-2 text-xs font-medium text-[#1A162F] outline-none transition-colors hover:border-[#C8D0E2] hover:bg-[#F8F9FC] focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 disabled:cursor-not-allowed disabled:border-[#E8EBF3] disabled:bg-[#F8F9FC] disabled:text-[#A4ABC0]"
                                    >
                                      {t(
                                        "website.brandsSettings.simple.manageApplicationAccess",
                                      )}
                                    </button>
                                  </span>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : null}
                {hasLoadedProjectConnectionGrants &&
                activeBusinessGroupMembers.length === 0 ? (
                  <div className="px-5 py-10 text-center">
                    <p className="text-sm font-medium text-[#1A162F]">
                      {t("website.brandsSettings.simple.noMembersInBrandTitle")}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-[#666F8D]">
                      {t(
                        "website.brandsSettings.simple.noMembersInBrandDescription",
                      )}
                    </p>
                  </div>
                ) : null}
                {!hasLoadedProjectConnectionGrants ? (
                  <div className="flex items-center justify-center gap-2 px-5 py-10 text-sm text-[#666F8D]">
                    {projectConnectionGrantsLoadError ? (
                      t(
                        "website.integrationsSettings.failedToLoadConnectionAccess",
                      )
                    ) : (
                      <>
                        <CircleNotch size={18} className="animate-spin" />
                        {t(
                          "website.integrationsSettings.loadingBusinessGroupMemberAccountAccess",
                        )}
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </Surface>
          ) : null}

          {activeTab === "settings" && settingsDraft && canEditActiveBrand ? (
            <div>
              <Surface className="p-5">
                <p className="text-xs leading-5 text-[#666F8D]">
                  {t("website.brandsSettings.simple.brandSettingsDescription")}
                </p>
                <div className="mt-5 space-y-4">
                  <div>
                    <span className="text-sm font-medium text-[#1A162F]">
                      {t("website.brandsSettings.simple.brandLogo")}
                    </span>
                    <div className="mt-3 flex items-center gap-4">
                      <BrandAvatar
                        brand={{
                          ...activeBrand,
                          name: settingsDraft.name,
                          logoUrl: settingsDraft.logoUrl,
                        }}
                        size="large"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={isLogoReading || !canEditActiveBrand}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-[10px] border border-[#E8EBF3] bg-white px-4 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:text-[#8A93B2]"
                          >
                            <Upload size={16} />
                            {isLogoReading
                              ? t("website.brandsSettings.simple.readingLogo")
                              : settingsDraft.logoUrl
                                ? t("website.brandsSettings.simple.replaceLogo")
                                : t("website.brandsSettings.simple.uploadLogo")}
                          </button>
                          {settingsDraft.logoUrl ? (
                            <button
                              type="button"
                              onClick={() =>
                                setSettingsDraft({
                                  ...settingsDraft,
                                  logoUrl: null,
                                })
                              }
                              className="inline-flex h-10 items-center justify-center rounded-[10px] px-3 text-sm font-medium text-[#B42318] transition-colors hover:bg-[#FEF3F2] hover:text-[#8F1D14]"
                              disabled={!canEditActiveBrand}
                            >
                              {t("website.brandsSettings.simple.removeLogo")}
                            </button>
                          ) : null}
                        </div>
                        <p className="mt-1.5 text-xs text-[#8A93B2]">
                          {t("website.brandsSettings.simple.logoUploadHint")}
                        </p>
                      </div>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={handleLogoUpload}
                        disabled={!canEditActiveBrand}
                        className="hidden"
                      />
                    </div>
                  </div>
                  <label className="block">
                    <span className="text-sm font-medium text-[#1A162F]">
                      {t("website.brandsSettings.brandName")}
                    </span>
                    <input
                      value={settingsDraft.name}
                      disabled={!canEditActiveBrand}
                      onChange={(event) =>
                        setSettingsDraft({
                          ...settingsDraft,
                          name: event.target.value,
                        })
                      }
                      className="mt-2 h-10 w-full rounded-[10px] border border-[#C8D0E2] bg-white px-3 text-sm text-[#1A162F] outline-none focus:border-[#1A162F]"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-[#1A162F]">
                      {t("website.brandsSettings.simple.website")}
                    </span>
                    <input
                      value={settingsDraft.domain}
                      disabled={!canEditActiveBrand}
                      onChange={(event) =>
                        setSettingsDraft({
                          ...settingsDraft,
                          domain: event.target.value,
                        })
                      }
                      placeholder={t(
                        "website.brandsSettings.simple.websitePlaceholder",
                      )}
                      className="mt-2 h-10 w-full rounded-[10px] border border-[#C8D0E2] bg-white px-3 text-sm text-[#1A162F] outline-none focus:border-[#1A162F]"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-[#1A162F]">
                      {t("website.brandsSettings.simple.brandDescription")}
                    </span>
                    <textarea
                      value={settingsDraft.description}
                      disabled={!canEditActiveBrand}
                      onChange={(event) =>
                        setSettingsDraft({
                          ...settingsDraft,
                          description: event.target.value,
                        })
                      }
                      rows={4}
                      maxLength={200}
                      placeholder={t(
                        "website.brandsSettings.simple.brandDescriptionPlaceholder",
                      )}
                      className="mt-2 w-full resize-none rounded-[10px] border border-[#C8D0E2] bg-white px-3 py-2.5 text-sm text-[#1A162F] outline-none focus:border-[#1A162F]"
                    />
                    <span className="mt-1 block text-right text-xs text-[#8A93B2]">
                      {settingsDraft.description.length}/200
                    </span>
                  </label>
                  {canEditActiveBrand && hasSettingsChanges ? (
                    <PrimaryButton
                      onClick={handleSaveSettings}
                      disabled={!settingsDraft.name.trim() || isLogoReading}
                      isLoading={isSavingProjectAction}
                    >
                      {t("website.brandsSettings.simple.saveBrandSettings")}
                    </PrimaryButton>
                  ) : null}
                </div>
              </Surface>
              {!isActiveBrandDefault && canDeleteActiveBrand ? (
                <Surface className="mt-4 border-[#F3D0CE] bg-[#FEFCFC] px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h2 className="text-base font-medium text-[#1A162F]">
                        {t("website.brandsSettings.simple.deleteBrandTitle")}
                      </h2>
                      <p className="mt-1 text-xs leading-5 text-[#666F8D]">
                        {t(
                          "website.brandsSettings.simple.deleteBrandDescription",
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDialog({ kind: "delete-brand" })}
                      className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-[10px] border border-[#F3D0CE] bg-white px-4 text-sm font-medium text-[#B42318] transition-colors hover:bg-[#FEF3F2] disabled:cursor-not-allowed disabled:border-[#E8EBF3] disabled:text-[#8A93B2]"
                    >
                      <Trash size={16} />
                      {t("website.brandsSettings.simple.deleteBrand")}
                    </button>
                  </div>
                </Surface>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      <Modal
        isOpen={dialog.kind === "create-brand"}
        onClose={closeCreateBrandDialog}
        title={t("website.brandsSettings.createBrand")}
        width="max-w-[560px]"
        showHeaderDivider
      >
        <div className="space-y-5">
          <div>
            <span className="text-sm font-medium text-[#1A162F]">
              {t("website.brandsSettings.simple.brandLogoOptional")}
            </span>
            <div className="mt-2.5 flex items-center gap-4">
              <BrandAvatar
                brand={{
                  id: "new-brand",
                  name: brandName || "B",
                  domain: brandDomain,
                  market: brandMarket,
                  description: brandDescription,
                  logoUrl: brandLogoUrl,
                  accent: "#2563EB",
                }}
                size="large"
              />
              <div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => createLogoInputRef.current?.click()}
                    disabled={isLogoReading}
                    className="inline-flex h-9 items-center justify-center gap-2 rounded-[10px] border border-[#E8EBF3] bg-white px-3.5 text-xs font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:text-[#8A93B2]"
                  >
                    <Upload size={15} />
                    {isLogoReading
                      ? t("website.brandsSettings.simple.readingLogo")
                      : brandLogoUrl
                        ? t("website.brandsSettings.simple.replaceLogo")
                        : t("website.brandsSettings.simple.uploadLogo")}
                  </button>
                  {brandLogoUrl ? (
                    <button
                      type="button"
                      onClick={() => setBrandLogoUrl(null)}
                      className="inline-flex h-9 items-center justify-center rounded-[10px] px-3 text-xs font-medium text-[#B42318] transition-colors hover:bg-[#FEF3F2] hover:text-[#8F1D14]"
                    >
                      {t("website.brandsSettings.simple.removeLogo")}
                    </button>
                  ) : null}
                </div>
                <p className="mt-1.5 text-xs text-[#8A93B2]">
                  {t("website.brandsSettings.simple.logoUploadHint")}
                </p>
              </div>
              <input
                ref={createLogoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleCreateLogoUpload}
                className="hidden"
              />
            </div>
          </div>

          <div className="border-t border-[#E8EBF3] pt-4">
            <label className="block">
              <span className="text-sm font-medium text-[#1A162F]">
                {t("website.brandsSettings.brandName")}
                <span className="ml-1 text-red-500">*</span>
              </span>
              <input
                autoFocus
                required
                aria-required="true"
                aria-invalid={hasBrandNameConflict}
                aria-describedby={
                  hasBrandNameConflict ? "business-group-name-error" : undefined
                }
                value={brandName}
                onChange={(event) => setBrandName(event.target.value)}
                placeholder={t("website.brandsSettings.brandNamePlaceholder")}
                className={clsx(
                  "mt-2 h-10 w-full rounded-[10px] border bg-white px-3 text-sm text-[#1A162F] outline-none",
                  hasBrandNameConflict
                    ? "border-[#DC2626] focus:border-[#DC2626] focus:ring-2 focus:ring-[#DC2626]/15"
                    : "border-[#C8D0E2] focus:border-[#1A162F]",
                )}
              />
              {hasBrandNameConflict ? (
                <p
                  id="business-group-name-error"
                  className="mt-1.5 text-xs text-[#DC2626]"
                >
                  {t(
                    "website.brandsSettings.simple.businessGroupNameAlreadyInUse",
                  )}
                </p>
              ) : null}
            </label>
          </div>

          <div className="border-t border-[#E8EBF3] pt-4">
            <span className="block text-sm font-medium text-[#1A162F]">
              {t("website.brandsSettings.simple.projectScope")}
            </span>
            <div className="mt-2.5 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setBrandScope("workspace")}
                disabled={!canCreateWorkspaceProject}
                className={clsx(
                  "flex flex-col items-start rounded-xl border p-3.5 text-left transition-all",
                  brandScope === "workspace"
                    ? "border-[#1A162F] bg-[#F8F9FC] shadow-sm"
                    : "border-[#E8EBF3] bg-white hover:border-[#C8D0E2]",
                  !canCreateWorkspaceProject && "cursor-not-allowed opacity-60",
                )}
              >
                <div className="flex items-center gap-1.5">
                  <Users
                    size={16}
                    weight="regular"
                    className="text-[#1A162F] shrink-0"
                  />
                  <span className="text-xs font-semibold text-[#1A162F]">
                    {t("website.brandsSettings.simple.scopeWorkspace")}
                  </span>
                </div>
                <p className="mt-2 text-[11px] leading-4 text-[#666F8D]">
                  {t("website.brandsSettings.simple.scopeWorkspaceDesc")}
                </p>
              </button>

              <button
                type="button"
                onClick={() => setBrandScope("private")}
                className={clsx(
                  "flex flex-col items-start rounded-xl border p-3.5 text-left transition-all",
                  brandScope === "private"
                    ? "border-[#1A162F] bg-[#F8F9FC] shadow-sm"
                    : "border-[#E8EBF3] bg-white hover:border-[#C8D0E2]",
                )}
              >
                <div className="flex items-center gap-1.5">
                  <LockKey
                    size={16}
                    weight="regular"
                    className="text-[#1A162F] shrink-0"
                  />
                  <span className="text-xs font-semibold text-[#1A162F]">
                    {t("website.brandsSettings.simple.scopePrivate")}
                  </span>
                </div>
                <p className="mt-2 text-[11px] leading-4 text-[#666F8D]">
                  {t("website.brandsSettings.simple.scopePrivateDesc")}
                </p>
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-[#E8EBF3] pt-4">
            <SecondaryButton
              onClick={closeCreateBrandDialog}
              disabled={isCreatingBrand}
            >
              {t("website.brandsSettings.cancel")}
            </SecondaryButton>
            <ProjectCreatePermissionTooltip
              blocked={projectCreatePermissionDenied}
              content={projectCreatePermissionMessage}
            >
              <PrimaryButton
                onClick={() => void handleCreateBrand()}
                disabled={
                  !canCreateProject ||
                  !brandName.trim() ||
                  hasBrandNameConflict ||
                  isLogoReading ||
                  isCreatingBrand
                }
                isLoading={isCreatingBrand}
              >
                {t("website.brandsSettings.create")}
              </PrimaryButton>
            </ProjectCreatePermissionTooltip>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={dialog.kind === "edit-brand" && Boolean(targetBrandForEdit)}
        onClose={() => setDialog({ kind: "closed" })}
        title={t("website.brandsSettings.simple.editBrand")}
        width="max-w-[560px]"
        showHeaderDivider
      >
        {targetBrandForEdit && settingsDraft ? (
          <div className="space-y-5">
            <div>
              <span className="text-sm font-medium text-[#1A162F]">
                {t("website.brandsSettings.simple.brandLogoOptional")}
              </span>
              <div className="mt-2.5 flex items-center gap-4">
                <BrandAvatar
                  brand={{
                    ...targetBrandForEdit,
                    name: settingsDraft.name,
                    logoUrl: settingsDraft.logoUrl,
                  }}
                  size="large"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={isLogoReading}
                      className="inline-flex h-9 items-center justify-center gap-2 rounded-[10px] border border-[#E8EBF3] bg-white px-3.5 text-xs font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:cursor-not-allowed disabled:text-[#8A93B2]"
                    >
                      <Upload size={15} />
                      {isLogoReading
                        ? t("website.brandsSettings.simple.readingLogo")
                        : settingsDraft.logoUrl
                          ? t("website.brandsSettings.simple.replaceLogo")
                          : t("website.brandsSettings.simple.uploadLogo")}
                    </button>
                    {settingsDraft.logoUrl ? (
                      <button
                        type="button"
                        onClick={() =>
                          setSettingsDraft({ ...settingsDraft, logoUrl: null })
                        }
                        className="inline-flex h-9 items-center justify-center rounded-[10px] px-3 text-xs font-medium text-[#B42318] transition-colors hover:bg-[#FEF3F2] hover:text-[#8F1D14]"
                      >
                        {t("website.brandsSettings.simple.removeLogo")}
                      </button>
                    ) : null}
                  </div>
                  <p className="mt-1.5 text-xs text-[#8A93B2]">
                    {t("website.brandsSettings.simple.logoUploadHint")}
                  </p>
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>
            </div>

            <div className="border-t border-[#E8EBF3] pt-4">
              <label className="block">
                <span className="text-sm font-medium text-[#1A162F]">
                  {t("website.brandsSettings.brandName")}
                  <span className="ml-1 text-red-500">*</span>
                </span>
                <input
                  autoFocus
                  required
                  aria-required="true"
                  value={settingsDraft.name}
                  onChange={(event) =>
                    setSettingsDraft({
                      ...settingsDraft,
                      name: event.target.value,
                    })
                  }
                  className="mt-2 h-10 w-full rounded-[10px] border border-[#C8D0E2] bg-white px-3 text-sm text-[#1A162F] outline-none focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/20"
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 border-t border-[#E8EBF3] pt-4">
              <SecondaryButton onClick={() => setDialog({ kind: "closed" })}>
                {t("website.brandsSettings.cancel")}
              </SecondaryButton>
              <PrimaryButton
                onClick={handleSaveProjectIdentity}
                disabled={
                  !settingsDraft.name.trim() ||
                  !hasProjectIdentityChanges ||
                  isLogoReading
                }
                isLoading={isSavingProjectAction}
              >
                {t("website.brandsSettings.simple.saveBrandSettings")}
              </PrimaryButton>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={dialog.kind === "brand-limit"}
        onClose={() => setDialog({ kind: "closed" })}
        title={t("website.brandsSettings.simple.limitReachedTitle")}
        width="max-w-[480px]"
        showHeaderDivider
      >
        <div className="space-y-5">
          <p className="text-sm leading-6 text-[#666F8D]">
            {t("website.brandsSettings.simple.limitReachedDescription", {
              limit: brandGroupAccess.limit,
            })}
          </p>
          {billingPermissionHint ? (
            <p className="rounded-[10px] bg-[#F8F9FC] px-3 py-2.5 text-xs leading-5 text-[#666F8D]">
              {billingPermissionHint}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <SecondaryButton onClick={() => setDialog({ kind: "closed" })}>
              {t("website.brandsSettings.cancel")}
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                setDialog({ kind: "closed" });
                openBrandGroupPricing();
              }}
            >
              {t("website.brandsSettings.simple.upgradePlan")}
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      {dialog.kind === "grant-access" && activeBrand ? (
        <DetailDrawer
          ariaLabel={
            isBusinessGroupInviteMode
              ? t("website.brandsSettings.simple.inviteNewMemberTitle")
              : dialog.userId
                ? t(
                    "website.brandsSettings.simple.manageApplicationAccessTitle",
                    {
                      name: selectedAccessUser?.name || "",
                    },
                  )
                : t(
                    isActiveBrandDefault
                      ? "website.brandsSettings.simple.assignApplicationAccessTitle"
                      : "website.brandsSettings.simple.addAndAssignAccess",
                  )
          }
          closeLabel={t("common.close")}
          size="default"
          header={
            <div>
              <h2 className="text-lg font-medium tracking-tight text-[#1A162F]">
                {isBusinessGroupInviteMode
                  ? t("website.brandsSettings.simple.inviteNewMemberTitle")
                  : dialog.userId
                    ? t(
                        "website.brandsSettings.simple.manageApplicationAccessTitle",
                        {
                          name: selectedAccessUser?.name || "",
                        },
                      )
                    : t(
                        isActiveBrandDefault
                          ? "website.brandsSettings.simple.assignApplicationAccessTitle"
                          : "website.brandsSettings.simple.addAndAssignAccess",
                      )}
              </h2>
              <p className="mt-1 text-xs text-[#666F8D]">
                {t(
                  isBusinessGroupInviteMode
                    ? "website.brandsSettings.simple.inviteNewMemberDescription"
                    : dialog.userId
                      ? "website.brandsSettings.simple.manageApplicationAccessDescription"
                      : isActiveBrandDefault
                        ? "website.brandsSettings.simple.defaultBrandGrantAccessDescription"
                        : "website.brandsSettings.simple.grantAccessDescription",
                )}
              </p>
            </div>
          }
          onClose={() => {
            if (dialogSubmissionRef.current === "idle") {
              setDialog({ kind: "closed" });
            }
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {dialog.userId ? (
                selectedAccessUser ? (
                  <div>
                    <p className="text-sm font-medium text-[#1A162F]">
                      {t("website.brandsSettings.simple.workspaceUser")}
                    </p>
                    <div className="mt-2 flex items-center gap-3 rounded-[10px] border border-[#E8EBF3] bg-[#F8F9FC] px-3 py-2.5">
                      <img
                        src={getMemberAvatarUrl(selectedAccessUser)}
                        alt=""
                        className="h-8 w-8 shrink-0 rounded-full border border-gray-200 bg-white object-cover"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-[#1A162F]">
                          {selectedAccessUser.name}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-[#8A93B2]">
                          {selectedAccessUser.email}
                        </span>
                      </span>
                    </div>
                  </div>
                ) : null
              ) : isBusinessGroupInviteMode ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-[#1A162F]">
                      {t("website.brandsSettings.simple.currentBusinessGroup")}
                    </p>
                    <div className="mt-2 flex h-14 items-center gap-3 rounded-[10px] border border-[#E8EBF3] bg-[#F8F9FC] px-4">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border border-[#E1E6F0] bg-white text-[#1A162F]">
                        <Briefcase size={17} weight="regular" />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#1A162F]">
                        {activeBrand.name}
                      </span>
                      <span className="shrink-0 rounded-full bg-[#EEF0F5] px-2 py-1 text-[10px] font-medium text-[#667085]">
                        {t(
                          "website.brandsSettings.simple.preselectedBusinessGroup",
                        )}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="business-group-invite-email"
                      className="mb-1.5 block text-sm font-medium text-[#1A162F]"
                    >
                      {t("website.membersSettings.emailAddress")}
                    </label>
                    <input
                      id="business-group-invite-email"
                      type="email"
                      value={inviteEmail}
                      placeholder={t(
                        "website.membersSettings.inviteEmailPlaceholder",
                      )}
                      disabled={isInvitingBusinessGroupMember}
                      autoFocus
                      onChange={(event) => {
                        setInviteEmail(event.target.value);
                        if (inviteEmailError) {
                          setInviteEmailError("");
                        }
                      }}
                      className={clsx(
                        "h-11 w-full rounded-[10px] border bg-white px-3.5 text-sm text-[#1A162F] outline-none transition-all placeholder:text-[#8A93B2] focus:ring-2 focus:ring-[#1A162F]/15 disabled:cursor-not-allowed disabled:bg-[#F8F9FC]",
                        inviteEmailError
                          ? "border-red-300 focus:border-red-500"
                          : "border-[#C8D0E2] focus:border-[#1A162F]",
                      )}
                    />
                    {inviteEmailError ? (
                      <p className="mt-1.5 text-xs text-red-600">
                        {inviteEmailError}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label
                      htmlFor="business-group-invite-role"
                      className="mb-1.5 block text-sm font-medium text-[#1A162F]"
                    >
                      {t("website.membersSettings.workspaceRole")}
                    </label>
                    {inviteRoleLoadState === "loading" ? (
                      <div
                        className="flex h-11 items-center gap-2 rounded-[10px] border border-[#E1E6F0] bg-[#F8F9FC] px-3.5 text-sm text-[#667085]"
                        role="status"
                      >
                        <CircleNotch size={16} className="animate-spin" />
                        {t("website.brandsSettings.simple.loadingInviteRoles")}
                      </div>
                    ) : inviteRoleLoadState === "error" ? (
                      <div className="flex min-h-11 items-center gap-2 rounded-[10px] border border-red-200 bg-red-50 px-3.5 py-2 text-sm text-red-700">
                        <WarningCircle size={16} className="shrink-0" />
                        {t(
                          "website.brandsSettings.simple.inviteRoleUnavailable",
                        )}
                      </div>
                    ) : (
                      <div className="relative">
                        <select
                          id="business-group-invite-role"
                          value={inviteRoleId}
                          disabled={
                            isInvitingBusinessGroupMember ||
                            inviteRoleLoadState !== "ready"
                          }
                          onChange={(event) =>
                            setInviteRoleId(event.target.value)
                          }
                          className="h-11 w-full appearance-none rounded-[10px] border border-[#C8D0E2] bg-white px-3.5 pr-10 text-sm text-[#1A162F] outline-none transition-all focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/15 disabled:cursor-not-allowed disabled:bg-[#F8F9FC]"
                        >
                          {inviteRoleOptions.map((role) => (
                            <option key={role.id} value={String(role.id)}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                        <CaretDown
                          size={14}
                          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[#7C86A6]"
                        />
                      </div>
                    )}
                  </div>

                  <p className="rounded-[10px] bg-[#F8F9FC] px-3.5 py-3 text-xs leading-5 text-[#666F8D]">
                    {t("website.brandsSettings.simple.inviteMembershipNotice")}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <span className="text-sm font-medium text-[#1A162F]">
                    {t("website.brandsSettings.simple.workspaceUser")}
                  </span>
                  {workspaceMembers.isLoading ? (
                    <div
                      className="flex h-14 items-center gap-2 rounded-[10px] border border-[#E1E6F0] bg-[#F8F9FC] px-4 text-sm text-[#667085]"
                      role="status"
                    >
                      <CircleNotch size={16} className="animate-spin" />
                      {t("website.brandsSettings.simple.loadingWorkspaceUsers")}
                    </div>
                  ) : workspaceMembersLoadError ? (
                    <div className="flex min-h-14 items-center justify-between gap-3 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3">
                      <span className="flex min-w-0 items-center gap-2.5 text-sm font-medium text-red-700">
                        <WarningCircle size={18} className="shrink-0" />
                        <span className="truncate">
                          {t("website.membersSettings.unableToLoadUsers")}
                        </span>
                      </span>
                      <SecondaryButton onClick={handleRetryWorkspaceUsers}>
                        {t("common.retry")}
                      </SecondaryButton>
                    </div>
                  ) : hasAssignableWorkspaceUser ? (
                    <WorkspaceUserSelect
                      value={memberToAdd}
                      options={workspaceUserOptions}
                      onChange={(userId) => {
                        setMemberToAdd(userId);
                        loadConnectionAccessDraft(userId);
                      }}
                      ariaLabel={t(
                        "website.brandsSettings.simple.workspaceUser",
                      )}
                      placeholder={t(
                        "website.brandsSettings.simple.selectWorkspaceMember",
                      )}
                      disabledLabel={t(
                        "website.brandsSettings.simple.alreadyAddedToBrand",
                      )}
                      inviteNewMemberLabel={
                        canInviteNewWorkspaceMemberToActiveBusinessGroup
                          ? t(
                              "website.brandsSettings.simple.inviteNewWorkspaceMember",
                            )
                          : undefined
                      }
                      onInviteNewMember={
                        canInviteNewWorkspaceMemberToActiveBusinessGroup
                          ? openInviteBusinessGroupMember
                          : undefined
                      }
                    />
                  ) : (
                    <div className="flex min-h-14 items-center justify-between gap-3 rounded-[10px] border border-dashed border-[#C8D0E2] bg-[#F8F9FC] px-4 py-3">
                      <span className="flex min-w-0 items-center gap-2.5 text-sm font-medium text-[#1A162F]">
                        <Users size={18} className="shrink-0 text-[#667085]" />
                        <span>{workspaceUserEmptyStateTitle}</span>
                      </span>
                      <SecondaryButton
                        onClick={
                          canInviteNewWorkspaceMemberToActiveBusinessGroup
                            ? openInviteBusinessGroupMember
                            : handleGoToWorkspaceUsers
                        }
                      >
                        {workspaceUserEmptyStateActionLabel}
                      </SecondaryButton>
                    </div>
                  )}
                </div>
              )}

              <div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-medium text-[#1A162F]">
                    {t("website.brandsSettings.simple.applicationAccounts")}
                  </p>
                  {canApplySameAccessToAllConnections ? (
                    <div className="flex flex-wrap items-center justify-end gap-2.5 sm:pr-8">
                      <span className="text-xs text-[#666F8D]">
                        {t(
                          "website.brandsSettings.simple.applyAccessToAllApplications",
                        )}
                      </span>
                      <div
                        className={clsx(
                          "grid items-center gap-1 rounded-[10px] border border-[#E1E6F0] bg-[#F4F5F8] p-1",
                          "w-[400px] grid-cols-3",
                        )}
                        role="group"
                        aria-label={t(
                          "website.brandsSettings.simple.applyAccessToAllApplications",
                        )}
                      >
                        {CONNECTION_ACCESS_LEVELS.map((accessLevel) => {
                          const appliesToAll = editableActiveConnections.every(
                            (connection) =>
                              (connectionAccessDraft[connection.id] ||
                                "no_access") === accessLevel,
                          );

                          return (
                            <button
                              key={accessLevel}
                              type="button"
                              onClick={() =>
                                setAllConnectionAccess(accessLevel)
                              }
                              className={clsx(
                                "h-8 min-w-0 whitespace-nowrap rounded-[7px] px-2 py-0 text-xs font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20",
                                appliesToAll &&
                                  "bg-[#1A162F] font-semibold text-white shadow-sm",
                                !appliesToAll &&
                                  "text-[#667085] hover:bg-white/60 hover:text-[#1A162F]",
                              )}
                            >
                              {t(
                                `website.brandsSettings.simple.accessLevels.${accessLevel}`,
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : shouldExplainIndividualConnectionAccess ? (
                    <p className="text-right text-xs leading-5 text-[#666F8D] sm:whitespace-nowrap">
                      {t(
                        "website.brandsSettings.simple.individualApplicationAccessNotice",
                      )}
                    </p>
                  ) : null}
                </div>
                <div className="mt-2 overflow-hidden rounded-[12px] border border-[#E8EBF3]">
                  {editableActiveConnections.length === 0 ? (
                    <div className="flex flex-col items-start gap-3 px-4 py-4">
                      <p className="text-xs leading-5 text-[#666F8D]">
                        {t(
                          "website.brandsSettings.simple.assignAppAccessLater",
                        )}
                      </p>
                    </div>
                  ) : (
                    editableActiveConnections.map((connection) => {
                      const application = getIntegrationBrandIcon(
                        connection.provider,
                      );
                      const selectedAccess =
                        connectionAccessDraft[connection.id] || "no_access";
                      const availableAccessLevels =
                        getConnectionAccessLevelsForCeiling(
                          connection,
                          memberToAdd,
                        );
                      const isConnectionOwner = isProjectConnectionOwner(
                        connection,
                        memberToAdd,
                      );
                      const connectorName =
                        getBrandConnectionOwnerDisplayName(
                          connection,
                          workspaceUsers,
                        ) ||
                        t("website.brandsSettings.simple.connectorUnknown");
                      const connectionMetaLine = formatConnectionMetaLine(
                        t,
                        locale,
                        {
                          connectedAt: connection.connectedAt,
                          connectedBy: connectorName,
                        },
                      );

                      return (
                        <div
                          key={connection.id}
                          className="grid grid-cols-1 items-center gap-x-5 gap-y-3 border-b border-[#E8EBF3] px-4 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_400px] sm:pr-8"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] border border-[#E8EBF3] bg-white">
                              <ApplicationLogo application={application} />
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-[#1A162F]">
                                {application.name}
                              </span>
                              <span className="mt-0.5 block truncate text-xs font-medium text-[#3D394D]">
                                {getConnectionDisplayName(connection)}
                              </span>
                              <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[#8A93B2]">
                                <span className="truncate">
                                  {connectionMetaLine}
                                </span>
                                {isConnectionOwner ? (
                                  <span className="inline-flex shrink-0 rounded-full bg-[#EEF2F8] px-2 py-0.5 text-[10px] font-medium text-[#52617F]">
                                    {t(
                                      "website.brandsSettings.simple.connectionOwnerBadge",
                                    )}
                                  </span>
                                ) : null}
                              </span>
                            </span>
                          </div>

                          <Tooltip
                            content={
                              isConnectionOwner
                                ? t(
                                    "website.brandsSettings.simple.connectionOwnerAccessTooltip",
                                  )
                                : undefined
                            }
                            side="top"
                          >
                            <span className="block min-w-0">
                              <ApplicationAccessPermissionControl
                                value={selectedAccess}
                                disabled={isConnectionOwner}
                                onChange={(accessLevel) =>
                                  setConnectionAccessDraft((current) => ({
                                    ...current,
                                    [connection.id]:
                                      normalizeDraftAccessForConnection(
                                        connection,
                                        accessLevel,
                                        memberToAdd,
                                      ),
                                  }))
                                }
                                accessLabels={{
                                  no_access: t(
                                    "website.brandsSettings.simple.accessLevels.no_access",
                                  ),
                                  read: t(
                                    "website.brandsSettings.simple.accessLevels.read",
                                  ),
                                  action: t(
                                    "website.brandsSettings.simple.accessLevels.action",
                                  ),
                                }}
                                availableAccessLevels={availableAccessLevels}
                                ariaLabel={`${getConnectionDisplayName(connection)} ${t("website.brandsSettings.simple.applicationAccess")}`}
                                canManageAccess={false}
                                onCanManageAccessChange={() => undefined}
                                manageAccessLabel=""
                                manageAccessHelp=""
                                showManageAccessControl={false}
                                className="w-full"
                                accessControlClassName={
                                  availableAccessLevels.length === 2
                                    ? "!w-full !grid-cols-2"
                                    : "!w-full !grid-cols-3"
                                }
                                accessButtonClassName="!h-8 !whitespace-nowrap !px-2 !py-0"
                              />
                            </span>
                          </Tooltip>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-2 border-t border-[#E8EBF3] bg-white px-6 py-4">
              <SecondaryButton
                onClick={() => {
                  if (isBusinessGroupInviteMode) {
                    returnToWorkspaceMemberSelection();
                  } else if (dialogSubmissionRef.current === "idle") {
                    setDialog({ kind: "closed" });
                  }
                }}
                disabled={
                  isAddingBusinessGroupMember || isInvitingBusinessGroupMember
                }
              >
                {t(
                  isBusinessGroupInviteMode
                    ? "website.brandsSettings.simple.backToWorkspaceMembers"
                    : "website.brandsSettings.cancel",
                )}
              </SecondaryButton>
              <PrimaryButton
                onClick={() =>
                  void (isBusinessGroupInviteMode
                    ? sendBusinessGroupMemberInvite()
                    : saveConnectionAccess())
                }
                disabled={
                  isAddingBusinessGroupMember ||
                  isInvitingBusinessGroupMember ||
                  (isBusinessGroupInviteMode
                    ? !inviteEmail.trim() ||
                      !inviteRoleId ||
                      inviteRoleLoadState !== "ready"
                    : !memberToAdd ||
                      (!dialog.userId &&
                        (isActiveBrandDefault
                          ? !hasPositiveConnectionAccessDraft
                          : !canSaveNewBusinessGroupMember)))
                }
                isLoading={
                  isAddingBusinessGroupMember || isInvitingBusinessGroupMember
                }
              >
                {t(
                  isBusinessGroupInviteMode
                    ? "website.brandsSettings.simple.sendBusinessGroupInvite"
                    : dialog.userId
                      ? "website.brandsSettings.simple.saveAppAccess"
                      : isActiveBrandDefault
                        ? "website.brandsSettings.simple.assignApplicationAccess"
                        : hasPositiveConnectionAccessDraft
                          ? "website.brandsSettings.simple.addAndAssignAccess"
                          : "website.brandsSettings.simple.addMemberWithoutAppAccess",
                )}
              </PrimaryButton>
            </div>
          </div>
        </DetailDrawer>
      ) : null}

      {dialog.kind === "batch-grant-access" && activeBrand ? (
        <DetailDrawer
          ariaLabel={t(
            "website.brandsSettings.simple.batchAssignPermissionsTitle",
          )}
          closeLabel={t("common.close")}
          size="default"
          header={
            <div>
              <h2 className="text-lg font-medium tracking-tight text-[#1A162F]">
                {t("website.brandsSettings.simple.batchAssignPermissionsTitle")}
              </h2>
              <p className="mt-1 text-xs text-[#666F8D]">
                {t("website.brandsSettings.simple.batchAssignPermissionsDesc")}
              </p>
            </div>
          }
          onClose={() => setDialog({ kind: "closed" })}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#1A162F]">
                  {t("website.brandsSettings.simple.selectApplicationToAssign")}
                </label>
                <ApplicationConnectionSelect
                  value={selectedBatchConnection?.id || ""}
                  options={
                    selectedBatchConnection ? [selectedBatchConnection] : []
                  }
                  onChange={() => undefined}
                  ariaLabel={t(
                    "website.brandsSettings.simple.selectApplicationToAssign",
                  )}
                  getConnectionOwnerLabel={(connection) => {
                    const connectorName = getBrandConnectionOwnerDisplayName(
                      connection,
                      workspaceUsers,
                    );
                    return t(
                      "website.brandsSettings.simple.connectedByMember",
                      {
                        name:
                          connectorName ||
                          t("website.brandsSettings.simple.connectorUnknown"),
                      },
                    );
                  }}
                  readOnly
                />
              </div>

              <div className="space-y-2.5">
                <div className="grid grid-cols-1 items-center gap-x-5 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto_400px] sm:pr-8">
                  <label className="text-sm font-medium text-[#1A162F]">
                    {t("website.brandsSettings.simple.selectMembersToAssign")}
                  </label>
                  {selectedBatchConnection &&
                  canApplySameBatchAccessToAllMembers ? (
                    <>
                      <span className="text-right text-xs text-[#666F8D]">
                        {t(
                          "website.brandsSettings.simple.applyAccessToAllApplications",
                        )}
                      </span>
                      <div
                        className="grid w-full grid-cols-3 items-center gap-1 rounded-[10px] border border-[#E1E6F0] bg-[#F4F5F8] p-1"
                        role="group"
                        aria-label={t(
                          "website.brandsSettings.simple.applyAccessToAllApplications",
                        )}
                      >
                        {CONNECTION_ACCESS_LEVELS.map((accessLevel) => {
                          const appliesToAll = batchAssignableMembers.every(
                            (member) =>
                              (batchMemberAccessDraft[member.id] ||
                                "no_access") === accessLevel,
                          );

                          return (
                            <button
                              key={accessLevel}
                              type="button"
                              onClick={() =>
                                setAllBatchMemberAccess(accessLevel)
                              }
                              className={clsx(
                                "h-8 min-w-0 whitespace-nowrap rounded-[7px] px-2 py-0 text-xs font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20",
                                appliesToAll &&
                                  "bg-[#1A162F] font-semibold text-white shadow-sm",
                                !appliesToAll &&
                                  "text-[#667085] hover:bg-white/60 hover:text-[#1A162F]",
                              )}
                            >
                              {t(
                                `website.brandsSettings.simple.accessLevels.${accessLevel}`,
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : shouldExplainIndividualBatchAccess ? (
                    <p className="text-right text-xs leading-5 text-[#666F8D] sm:col-span-2 sm:whitespace-nowrap">
                      {t(
                        "website.brandsSettings.simple.individualMemberAccessNotice",
                      )}
                    </p>
                  ) : null}
                </div>

                <div className="divide-y divide-[#E8EBF3] rounded-xl border border-[#E8EBF3] bg-white">
                  {batchAssignableMembers.map((member) => {
                    const selectedAccess =
                      batchMemberAccessDraft[member.id] || "no_access";
                    const availableAccessLevels = selectedBatchConnection
                      ? getConnectionAccessLevelsForCeiling(
                          selectedBatchConnection,
                          member.id,
                        )
                      : [];
                    return (
                      <div
                        key={member.id}
                        className="grid grid-cols-1 items-center gap-x-5 gap-y-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_400px] sm:pr-8"
                      >
                        <div className="flex min-w-0 items-center gap-2.5">
                          <img
                            src={getMemberAvatarUrl(member)}
                            alt=""
                            className="h-8 w-8 shrink-0 rounded-full border border-gray-200 bg-white object-cover"
                          />
                          <div className="min-w-0">
                            <span className="block truncate text-sm font-medium text-[#1A162F]">
                              {member.name}
                            </span>
                            <span className="mt-0.5 block truncate text-xs text-[#8A93B2]">
                              {member.email}
                            </span>
                          </div>
                        </div>
                        {selectedBatchConnection ? (
                          <ApplicationAccessPermissionControl
                            value={selectedAccess}
                            disabled={isSavingProjectAction}
                            onChange={(accessLevel) =>
                              setBatchMemberAccessDraft((current) => ({
                                ...current,
                                [member.id]: normalizeDraftAccessForConnection(
                                  selectedBatchConnection,
                                  accessLevel,
                                  member.id,
                                ),
                              }))
                            }
                            accessLabels={{
                              no_access: t(
                                "website.brandsSettings.simple.accessLevels.no_access",
                              ),
                              read: t(
                                "website.brandsSettings.simple.accessLevels.read",
                              ),
                              action: t(
                                "website.brandsSettings.simple.accessLevels.action",
                              ),
                            }}
                            availableAccessLevels={availableAccessLevels}
                            ariaLabel={`${member.name} ${t("website.brandsSettings.simple.applicationAccess")}`}
                            canManageAccess={false}
                            onCanManageAccessChange={() => undefined}
                            manageAccessLabel=""
                            manageAccessHelp=""
                            showManageAccessControl={false}
                            className="w-full"
                            accessControlClassName={
                              availableAccessLevels.length === 2
                                ? "!w-full !grid-cols-2"
                                : "!w-full !grid-cols-3"
                            }
                            accessButtonClassName="!h-8 !whitespace-nowrap !px-2 !py-0"
                          />
                        ) : null}
                      </div>
                    );
                  })}
                  {selectedBatchConnection &&
                  batchAssignableMembers.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-[#666F8D]">
                      {t(
                        "website.brandsSettings.simple.noOtherMembersToAssign",
                      )}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex shrink-0 justify-end gap-2 border-t border-[#E8EBF3] bg-white px-6 py-4">
              <SecondaryButton onClick={() => setDialog({ kind: "closed" })}>
                {t("website.brandsSettings.cancel")}
              </SecondaryButton>
              <PrimaryButton
                onClick={() => void saveBatchConnectionAccess()}
                disabled={
                  isSavingProjectAction ||
                  !selectedBatchConnection ||
                  batchAssignableMembers.length === 0
                }
                isLoading={isSavingProjectAction}
              >
                {t("website.brandsSettings.simple.applyBatchAccess")}
              </PrimaryButton>
            </div>
          </div>
        </DetailDrawer>
      ) : null}

      <MemberInviteUpgradeModal
        isOpen={isInviteUpgradeModalOpen}
        onClose={() => setIsInviteUpgradeModalOpen(false)}
        onPrimaryAction={handleOpenInviteUpgradePricing}
        prompt={workspaceInviteUpgradePrompt}
      />

      <MemberInviteLimitModal
        isOpen={isInviteLimitModalOpen}
        onClose={() => setIsInviteLimitModalOpen(false)}
        onPrimaryAction={handleInviteLimitPrimaryAction}
        prompt={workspaceInviteLimitPrompt}
      />

      <ContactSalesModal
        isOpen={isInviteLimitContactSalesOpen}
        onClose={() => setIsInviteLimitContactSalesOpen(false)}
      />

      <Modal
        isOpen={dialog.kind === "remove-connection" && Boolean(activeBrand)}
        onClose={() => setDialog({ kind: "closed" })}
        title={
          dialog.kind === "remove-connection" && activeBrand
            ? (() => {
                const connection = brandConnections.find(
                  (item) => item.id === dialog.connectionId,
                );
                return t(
                  "website.brandsSettings.simple.removeConnectionTitle",
                  {
                    app: connection ? getConnectionDisplayName(connection) : "",
                    brand: activeBrand.name,
                  },
                );
              })()
            : ""
        }
        width="max-w-[520px]"
        showHeaderDivider
      >
        {dialog.kind === "remove-connection" && activeBrand
          ? (() => {
              const connection = brandConnections.find(
                (item) => item.id === dialog.connectionId,
              );
              return connection ? (
                <div className="space-y-5">
                  <p className="text-sm leading-6 text-[#666F8D]">
                    {t(
                      "website.brandsSettings.simple.removeConnectionConfirmation",
                      {
                        app: getConnectionDisplayName(connection),
                        brand: activeBrand.name,
                      },
                    )}
                  </p>
                  <div className="flex justify-end gap-2">
                    <SecondaryButton
                      onClick={() => setDialog({ kind: "closed" })}
                    >
                      {t("website.brandsSettings.cancel")}
                    </SecondaryButton>
                    <DangerButton
                      onClick={() => void handleRemoveConnection()}
                      isLoading={isSavingProjectAction}
                    >
                      {t("website.brandsSettings.simple.removeFromBrand")}
                    </DangerButton>
                  </div>
                </div>
              ) : null;
            })()
          : null}
      </Modal>

      <Modal
        isOpen={dialog.kind === "delete-brand" && Boolean(targetBrandForDelete)}
        onClose={() => setDialog({ kind: "closed" })}
        title={t("website.brandsSettings.simple.deleteBrandTitle")}
        width="max-w-[520px]"
        showHeaderDivider
      >
        {dialog.kind === "delete-brand" && targetBrandForDelete ? (
          <div className="space-y-4">
            <p className="text-sm leading-6 text-[#666F8D]">
              {t("website.brandsSettings.simple.deleteBrandConfirmation", {
                brand: targetBrandForDelete.name,
              })}
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <SecondaryButton onClick={() => setDialog({ kind: "closed" })}>
                {t("website.brandsSettings.cancel")}
              </SecondaryButton>
              <DangerButton
                onClick={() => void handleDeleteBrand()}
                isLoading={isSavingProjectAction}
              >
                {t("website.brandsSettings.simple.deleteBrand")}
              </DangerButton>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default BrandsSettings;
